<template>
  <div id="app">
    <!-- Record Details Popup -->
    <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
      <div class="popup-content wide-popup">
        <div class="popup-header">
          <h3>Transaction Details</h3>
          <button @click="selectedRecord = null" class="close-btn">×</button>
        </div>
        <div class="popup-body">
          <div class="detail-sections">
            <!-- Transaction Info -->
            <div class="detail-section">
              <h4>Transaction Information</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>Transaction ID:</strong> {{ selectedRecord.unqTrID }}
                </div>
                <div class="detail-row">
                  <strong>Date:</strong> {{ formatDateTime(selectedRecord.transactionDate) }}
                </div>
                <div class="detail-row">
                  <strong>Store:</strong> {{ selectedRecord.store }}
                </div>
                <div class="detail-row">
                  <strong>Product:</strong> {{ selectedRecord.product }}
                </div>
              </div>
            </div>

            <!-- Equipment Info -->
            <div class="detail-section equipment-section">
              <h4><span class="source-icon">🚛</span> Equipment Details</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>Equipment:</strong> {{ selectedRecord.equipment }}
                </div>
                <div class="detail-row">
                  <strong>Registration:</strong> {{ selectedRecord.registration }}
                </div>
                <div class="detail-row">
                  <strong>Tag:</strong> {{ selectedRecord.eqpTag }}
                </div>
                <div class="detail-row">
                  <strong>Master Equipment:</strong> {{ selectedRecord.masterEquipment || 'N/A' }}
                </div>
              </div>
            </div>

            <!-- Volume & Cost -->
            <div class="detail-section cost-section">
              <h4><span class="source-icon">💰</span> Volume & Cost</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>Volume:</strong> {{ formatVolume(selectedRecord.volume) }} L
                </div>
                <div class="detail-row">
                  <strong>Fuel Price:</strong> ZAR {{ formatCurrency(selectedRecord.fuelPrice) }}
                </div>
                <div class="detail-row">
                  <strong>Total Cost:</strong> ZAR {{ formatCurrency(selectedRecord.totalCost) }}
                </div>
                <div class="detail-row">
                  <strong>Consumption:</strong> {{ selectedRecord.consumption }}
                </div>
              </div>
            </div>

            <!-- Personnel Info -->
            <div class="detail-section personnel-section">
              <h4><span class="source-icon">👥</span> Personnel</h4>
              <div class="detail-grid">
                <div class="detail-row">
                  <strong>Driver:</strong> {{ selectedRecord.driver || 'Not Assigned' }}
                </div>
                <div class="detail-row">
                  <strong>Fuel Attendant:</strong> {{ selectedRecord.fuelAttendant || 'Not Assigned' }}
                </div>
                <div class="detail-row">
                  <strong>Cost Centre:</strong> {{ selectedRecord.costCentre || 'N/A' }}
                </div>
                <div class="detail-row">
                  <strong>Job Number:</strong> {{ selectedRecord.jobNumber }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drill-down Popup for Aggregated Views -->
    <div v-if="showDrillDown" class="simple-popup drill-down-overlay">
      <div class="popup-content drill-down-popup" 
           ref="drillDownPopup"
           :style="{ left: drillDownPosition.x + 'px', top: drillDownPosition.y + 'px' }">
        <div class="popup-header drill-down-header" 
             @mousedown="startDrag"
             @touchstart="startDrag">
          <div class="header-info">
            <h3>{{ drillDownTitle }}</h3>
            <span class="popup-date-range">Date: {{ formatDateForDisplay(filters.fromDate) }} - {{ formatDateForDisplay(filters.toDate) }}</span>
          </div>
          <button @click="closeDrillDown" class="close-btn" title="Close">×</button>
        </div>
        <div class="popup-body">
          <!-- Summary Section -->
          <div class="drill-down-summary">
            <div class="summary-card">
              <span class="summary-label">{{ drillDownGroup }}</span>
              <span class="summary-value-large">{{ drillDownData.groupName }}</span>
            </div>
            <div class="summary-stats">
              <div class="stat-item">
                <span class="stat-label">Total Volume:</span>
                <span class="stat-value">{{ formatVolume(drillDownData.totalVolume) }} L</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Transactions:</span>
                <span class="stat-value">{{ drillDownData.records.length }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Total Cost:</span>
                <span class="stat-value">ZAR {{ formatCurrency(drillDownData.totalCost) }}</span>
              </div>
            </div>
          </div>
          
          <!-- Export Options -->
          <div class="drill-down-actions">
            <button class="action-btn excel-btn" @click="exportDrillDownExcel">
              <span>📊</span> Excel Export
            </button>
            <button class="action-btn csv-btn" @click="exportDrillDownCSV">
              <span>📄</span> CSV Export
            </button>
            <button class="action-btn print-btn" @click="printDrillDown">
              <span>🖨️</span> Print
            </button>
          </div>
          
          <!-- Data Grid -->
          <div class="drill-down-grid">
            <table class="drill-down-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Store</th>
                  <th>Registration</th>
                  <th>Product</th>
                  <th>Driver</th>
                  <th>Fuel Attendant</th>
                  <th>Type</th>
                  <th>KM/Hour</th>
                  <th>Volume</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in drillDownData.records" :key="index" 
                    @click="showRecordDetails(record)"
                    class="drill-down-row">
                  <td>{{ formatDateTime(record.transactionDate) }}</td>
                  <td>
                    <span :class="['store-badge-small', getStoreClass(record.store)]">
                      {{ record.store }}
                    </span>
                  </td>
                  <td>
                    <code class="registration-code">{{ record.registration }}</code>
                  </td>
                  <td>
                    <span :class="['product-badge-small', getProductClass(record.product)]">
                      {{ record.product }}
                    </span>
                  </td>
                  <td>{{ record.driver || 'None' }}</td>
                  <td>{{ record.fuelAttendant || 'None' }}</td>
                  <td>{{ record.measurement || 'None' }}</td>
                  <td>{{ record.km || record.hour || '0' }}</td>
                  <td class="volume-cell-drill">{{ formatVolume(record.volume) }}</td>
                </tr>
              </tbody>
            </table>
            
            <!-- Totals Row -->
            <div class="drill-down-totals">
              <span class="total-label">Total Volume:</span>
              <span class="total-value">{{ formatVolume(drillDownData.totalVolume) }}</span>
            </div>
          </div>
          
          <!-- Pagination Info -->
          <div class="drill-down-footer">
            <span class="record-count">{{ drillDownData.records.length }} of {{ drillDownData.records.length }} items</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">⛽</span>
          </div>
          <h1 class="dashboard-title">FAMS Quick Dispensing Monitor</h1>
        </div>
        <div class="header-controls">
          <div class="loader-controls">
            <div class="loader-toggle">
              <label class="toggle-label">
                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                <span class="toggle-text">Auto Refresh</span>
              </label>
            </div>
            <div v-if="autoRefreshEnabled" class="refresh-interval">
              <label class="interval-label">
                <span class="interval-text">Every:</span>
                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                  <option :value="30000">30 sec</option>
                  <option :value="60000">1 min</option>
                  <option :value="300000">5 min</option>
                  <option :value="600000">10 min</option>
                </select>
              </label>
            </div>
            <button class="header-refresh-btn" @click="refreshData" title="Refresh Now">
              <span class="refresh-icon">🔄</span>
              <span>Refresh</span>
            </button>
          </div>
          <div class="live-indicator">
            <div :class="['live-dot', { 'auto-refresh': autoRefreshEnabled }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : 'Live' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Quick Dispensing Overview</h3>
          <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
            <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
            {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
          </button>
        </div>
        
        <transition name="slide-fade">
          <div v-if="statsVisible" class="stats-wrapper">
            <div class="stats-container">
              <!-- Total Transactions -->
              <div class="stat-card clickable-stat total-stat" @click="clearAllFilters">
                <div class="stat-label">Total Transactions</div>
                <div class="stat-value">{{ summaryStats.totalTransactions }}</div>
                <div class="stat-change positive">
                  <span class="stat-icon">📝</span>
                  <span>All records</span>
                </div>
                <div class="stat-hint">Click to show all</div>
              </div>
              
              <!-- Total Volume -->
              <div class="stat-card volume-stat">
                <div class="stat-label">Total Volume</div>
                <div class="stat-value" style="color: #4CAF50;">{{ formatVolume(summaryStats.totalVolume) }} L</div>
                <div class="stat-change">
                  <span class="stat-icon">⛽</span>
                  <span>Fuel dispensed</span>
                </div>
              </div>
              
              <!-- Total Cost -->
              <div class="stat-card cost-stat">
                <div class="stat-label">Total Cost</div>
                <div class="stat-value" style="color: #FF9800;">ZAR {{ formatCurrency(summaryStats.totalCost) }}</div>
                <div class="stat-change">
                  <span class="stat-icon">💰</span>
                  <span>Amount spent</span>
                </div>
              </div>
              
              <!-- Unique Equipment -->
              <div class="stat-card equipment-stat">
                <div class="stat-label">Equipment Count</div>
                <div class="stat-value" style="color: #2196F3;">{{ summaryStats.uniqueEquipment }}</div>
                <div class="stat-change">
                  <span class="stat-icon">🚛</span>
                  <span>Active equipment</span>
                </div>
              </div>
              
              <!-- Products Used -->
              <div class="stat-card product-stat">
                <div class="stat-label">Products</div>
                <div class="stat-value" style="color: #9C27B0;">{{ summaryStats.uniqueProducts }}</div>
                <div class="stat-change">
                  <span class="stat-icon">🛢️</span>
                  <span>Product types</span>
                </div>
              </div>
              
              <!-- Average Transaction -->
              <div class="stat-card average-stat">
                <div class="stat-label">Avg Transaction</div>
                <div class="stat-value" style="color: #E74C3C;">{{ formatVolume(summaryStats.avgVolume) }} L</div>
                <div class="stat-change">
                  <span class="stat-icon">📊</span>
                  <span>Per transaction</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      
      <!-- Filters Section -->
      <div class="search-section" :class="{ 'collapsed': !filtersVisible }">
        <div class="filter-header">
          <h3 class="filter-title">Data Filters & Reports</h3>
          <div class="filter-header-buttons">
            <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
              <span class="toggle-icon">{{ filtersVisible ? '📁' : '📂' }}</span>
              {{ filtersVisible ? 'Hide Filters' : 'Show Filters' }}
            </button>
            <button class="reset-filter-header-btn" @click="resetFilters" title="Reset all filters to defaults">
              <span class="reset-icon">↻</span>
              Reset Filters
            </button>
          </div>
        </div>
        
        <transition name="slide-fade">
          <div v-if="filtersVisible" class="search-controls-wrapper">
            <div class="search-controls">
              <!-- Date Range -->
              <div class="search-group">
                <label class="search-label">From Date</label>
                <input 
                  type="date" 
                  class="search-input"
                  v-model="filters.fromDate"
                  @change="applyFilters">
              </div>
              
              <div class="search-group">
                <label class="search-label">To Date</label>
                <input 
                  type="date" 
                  class="search-input"
                  v-model="filters.toDate"
                  @change="applyFilters">
              </div>
              
              <!-- Report Type -->
              <div class="search-group">
                <label class="search-label">Report Type</label>
                <select v-model="filters.reportType" @change="handleReportTypeChange" class="search-input">
                  <option value="general">General View</option>
                  <option value="general-price">General View - Price</option>
                  <option value="usage-product">Usage per Product</option>
                  <option value="usage-store">Usage per Store</option>
                  <option value="usage-equipment">Usage per Equipment</option>
                  <option value="usage-master-group">Usage per Master Group</option>
                  <option value="usage-driver">Usage per Driver</option>
                  <option value="usage-fuel-attendant">Usage per Fuel Attendant</option>
                  <option value="usage-allocations">Usage per Allocations</option>
                  <option value="usage-cost-centres">Usage per CostCentres</option>
                </select>
              </div>
              
              <!-- Product Multi-Select Filter -->
              <div class="search-group">
                <label class="search-label">
                  Product
                  <span class="multi-badge orange-badge">
                    ({{ filters.productIds.length }}/{{ uniqueProducts.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display" @click="toggleDropdown('products')">
                    <span v-if="filters.productIds.length === 0" class="placeholder">
                      All Products
                    </span>
                    <span v-else-if="filters.productIds.length === uniqueProducts.length" class="selected-items">
                      All products selected
                    </span>
                    <span v-else class="selected-items">
                      {{ filters.productIds.length }} product(s) selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.products" class="multi-select-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllProducts" :checked="filters.productIds.length === uniqueProducts.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="product in uniqueProducts" :key="product.id">
                        <input type="checkbox" :value="product.id" v-model="filters.productIds" @change="applyFilters">
                        <span>{{ product.name }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
                            
        <!-- Store Multi-Select Filter with smaller font class -->
<div class="search-group">
  <label class="search-label">
    Store
    <span class="multi-badge orange-badge">
      ({{ filters.storeIds.length }}/{{ uniqueStores.length }})
    </span>
  </label>
  <div class="multi-select-container store-multi-select">
    <div class="multi-select-display store-dropdown-display" @click="toggleDropdown('stores')">
      <span v-if="filters.storeIds.length === 0" class="placeholder">
        All Stores
      </span>
      <span v-else-if="filters.storeIds.length === uniqueStores.length" class="selected-items">
        All stores selected
      </span>
      <span v-else class="selected-items">
        {{ filters.storeIds.length }} store(s) selected
      </span>
      <span class="dropdown-arrow">▼</span>
    </div>
    <div v-if="dropdownStates.stores" class="multi-select-dropdown store-dropdown-options">
      <div class="select-all-option">
        <label class="checkbox-option">
          <input type="checkbox" @change="toggleAllStores" :checked="filters.storeIds.length === uniqueStores.length">
          <span>Select All</span>
        </label>
      </div>
      <div class="dropdown-options">
        <label class="checkbox-option" v-for="store in uniqueStores" :key="store.id">
          <input type="checkbox" :value="store.id" v-model="filters.storeIds" @change="applyFilters">
          <span class="store-option-text">{{ store.name }}</span>
        </label>
      </div>
    </div>
  </div>
</div>
                        
                    <!-- Equipment Multi-Select Filter -->
          <div class="search-group">
            <label class="search-label">
              Equipment
              <span class="multi-badge orange-badge">
                ({{ filters.equipmentIds.length }}/{{ uniqueEquipment.length }})
              </span>
            </label>
            <div class="multi-select-container">
              <div class="multi-select-display" @click="toggleDropdown('equipment')">
                <span v-if="filters.equipmentIds.length === 0" class="placeholder">
                  All Equipment
                </span>
                <span v-else-if="filters.equipmentIds.length === uniqueEquipment.length" class="selected-items">
                  All equipment selected
                </span>
                <span v-else class="selected-items">
                  {{ filters.equipmentIds.length }} equipment selected
                </span>
                <span class="dropdown-arrow">▼</span>
              </div>
              <div v-if="dropdownStates.equipment" class="multi-select-dropdown">
                <div class="select-all-option">
                  <label class="checkbox-option">
                    <input type="checkbox" @change="toggleAllEquipment" :checked="filters.equipmentIds.length === uniqueEquipment.length">
                    <span>Select All</span>
                  </label>
                </div>
                <div class="dropdown-options">
                  <label class="checkbox-option" v-for="equipment in uniqueEquipment" :key="equipment.id">
                    <input type="checkbox" :value="equipment.id" v-model="filters.equipmentIds" @change="applyFilters">
                    <span>{{ equipment.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
                        
            <!-- Master Equipment Multi-Select Filter -->
          <div class="search-group">
            <label class="search-label">
              Master Equipment
              <span class="multi-badge orange-badge">
                ({{ filters.masterEquipmentIds.length }}/{{ uniqueMasterEquipment.length }})
              </span>
            </label>
            <div class="multi-select-container">
              <div class="multi-select-display" @click="toggleDropdown('masterEquipment')">
                <span v-if="filters.masterEquipmentIds.length === 0" class="placeholder">
                  All Master Equipment
                </span>
                <span v-else-if="filters.masterEquipmentIds.length === uniqueMasterEquipment.length" class="selected-items">
                  All master equipment selected
                </span>
                <span v-else class="selected-items">
                  {{ filters.masterEquipmentIds.length }} selected
                </span>
                <span class="dropdown-arrow">▼</span>
              </div>
              <div v-if="dropdownStates.masterEquipment" class="multi-select-dropdown">
                <div class="select-all-option">
                  <label class="checkbox-option">
                    <input type="checkbox" @change="toggleAllMasterEquipment" :checked="filters.masterEquipmentIds.length === uniqueMasterEquipment.length">
                    <span>Select All</span>
                  </label>
                </div>
                <div class="dropdown-options">
                  <label class="checkbox-option" v-for="masterEquip in uniqueMasterEquipment" :key="masterEquip.id">
                    <input type="checkbox" :value="masterEquip.id" v-model="filters.masterEquipmentIds" @change="applyFilters">
                    <span>{{ masterEquip.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>              
          <!-- Allocation Multi-Select Filter -->
          <div class="search-group">
            <label class="search-label">
              Allocation 1
              <span class="multi-badge orange-badge">
                ({{ filters.allocation1Ids.length }}/{{ uniqueAllocations1.length }})
              </span>
            </label>
            <div class="multi-select-container">
              <div class="multi-select-display" @click="toggleDropdown('allocations')">
                <span v-if="filters.allocation1Ids.length === 0" class="placeholder">
                  All Allocations
                </span>
                <span v-else-if="filters.allocation1Ids.length === uniqueAllocations1.length" class="selected-items">
                  All allocations selected
                </span>
                <span v-else class="selected-items">
                  {{ filters.allocation1Ids.length }} allocation(s) selected
                </span>
                <span class="dropdown-arrow">▼</span>
              </div>
              <div v-if="dropdownStates.allocations" class="multi-select-dropdown">
                <div class="select-all-option">
                  <label class="checkbox-option">
                    <input type="checkbox" @change="toggleAllAllocations" :checked="filters.allocation1Ids.length === uniqueAllocations1.length">
                    <span>Select All</span>
                  </label>
                </div>
                <div class="dropdown-options">
                  <label class="checkbox-option" v-for="allocation in uniqueAllocations1" :key="allocation.id">
                    <input type="checkbox" :value="allocation.id" v-model="filters.allocation1Ids" @change="applyFilters">
                    <span>{{ allocation.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Cost Centre Multi-Select Filter -->
          <div class="search-group">
            <label class="search-label">
              Cost Centre
              <span class="multi-badge orange-badge">
                ({{ filters.costCentreIds.length }}/{{ uniqueCostCentres.length }})
              </span>
            </label>
            <div class="multi-select-container">
              <div class="multi-select-display" @click="toggleDropdown('costCentres')">
                <span v-if="filters.costCentreIds.length === 0" class="placeholder">
                  All Cost Centres
                </span>
                <span v-else-if="filters.costCentreIds.length === uniqueCostCentres.length" class="selected-items">
                  All cost centres selected
                </span>
                <span v-else class="selected-items">
                  {{ filters.costCentreIds.length }} cost centre(s) selected
                </span>
                <span class="dropdown-arrow">▼</span>
              </div>
              <div v-if="dropdownStates.costCentres" class="multi-select-dropdown">
                <div class="select-all-option">
                  <label class="checkbox-option">
                    <input type="checkbox" @change="toggleAllCostCentres" :checked="filters.costCentreIds.length === uniqueCostCentres.length">
                    <span>Select All</span>
                  </label>
                </div>
                <div class="dropdown-options">
                  <label class="checkbox-option" v-for="costCentre in uniqueCostCentres" :key="costCentre.id">
                    <input type="checkbox" :value="costCentre.id" v-model="filters.costCentreIds" @change="applyFilters">
                    <span>{{ costCentre.name }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>              
              <!-- Visible Columns Filter -->
              <div class="search-group">
                <label class="search-label">
                  Visible Columns 
                  <span class="multi-badge orange-badge">
                    ({{ selectedColumns.length }}/{{ availableColumns.length }})
                  </span>
                </label>
                <div class="multi-select-container">
                  <div class="multi-select-display columns-select" @click="toggleDropdown('columns')">
                    <span v-if="selectedColumns.length === availableColumns.length" class="selected-items">
                      All columns selected
                    </span>
                    <span v-else class="selected-items">
                      {{ selectedColumns.length }} columns selected
                    </span>
                    <span class="dropdown-arrow">▼</span>
                  </div>
                  <div v-if="dropdownStates.columns" class="multi-select-dropdown columns-dropdown">
                    <div class="select-all-option">
                      <label class="checkbox-option">
                        <input type="checkbox" @change="toggleAllColumns" :checked="selectedColumns.length === availableColumns.length">
                        <span>Select All</span>
                      </label>
                    </div>
                    <div class="dropdown-options">
                      <label class="checkbox-option" v-for="column in availableColumns" :key="column.key">
                        <input type="checkbox" :value="column.key" v-model="selectedColumns" @change="handleColumnToggle">
                        <span>{{ column.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="filter-actions">
              <button class="apply-filters-btn" @click="applyFilters">
                Apply Filters
              </button>
              <button class="reset-filters-btn" @click="resetFilters">
                Reset
              </button>
            </div>
          </div>
        </transition>
      </div>
      
      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">
            <span class="report-name">{{ getReportTitle() }}</span>
            <span class="date-range-label">Date: {{ formatDateForDisplay(filters.fromDate) }} - {{ formatDateForDisplay(filters.toDate) }}</span>
          </div>
          <div class="grid-controls">
            <!-- Clear Column Searches Button - Only show in general views if there are active searches -->
            <button 
              v-if="(filters.reportType === 'general' || filters.reportType === 'general-price') && hasActiveColumnSearches"
              class="clear-column-searches-btn"
              @click="clearColumnSearches"
              title="Clear all column searches">
              <span class="action-icon">✖</span>
              <span>Clear Searches</span>
            </button>
            <div class="rows-selector">
              <span class="rows-label">Show:</span>
              <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                <option :value="10">10 rows</option>
                <option :value="15">15 rows</option>
                <option :value="25">25 rows</option>
                <option :value="50">50 rows</option>
                <option :value="100">100 rows</option>
                <option :value="200">200 rows</option>
              </select>
            </div>
            <div class="grid-actions">
              <button class="action-button popout-btn" @click="popOutTable" title="Pop Out">
                <span class="action-icon">🔲</span>
                <span>Pop Out</span>
              </button>
              <button class="action-button refresh-btn" @click="refreshData" title="Refresh">
                <span class="action-icon">🔄</span>
                <span>Refresh</span>
              </button>
              <button class="action-button export-btn" @click="exportData" title="Export to CSV">
                <span class="action-icon">📥</span>
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
        
        <div class="table-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>
          
          <table v-if="!loading && displayData.length > 0" class="data-table">
            <thead>
              <tr>
                <th v-for="column in visibleColumns" :key="column.key">
                  {{ column.label }}
                </th>
              </tr>
                <!-- Search Row - Only for general views -->
                <tr v-if="filters.reportType === 'general' || filters.reportType === 'general-price'" class="search-row">
                  <th v-for="column in visibleColumns" :key="'search-' + column.key" class="search-cell">
                    <input 
                      type="text" 
                      class="column-search-input"
                      :placeholder="'Search...'"
                      v-model="columnSearchFilters[column.key]"
                      @input="handleColumnSearch"
                      @keyup.enter="handleColumnSearch"
                    />
                  </th>
                </tr>
            </thead>
            <tbody>
              <tr v-for="record in paginatedRecords" :key="record.id || record.groupKey" 
                  @click="showRecordDetails(record)" 
                  :class="getRowClass(record)">
                
                <td v-for="column in visibleColumns" :key="column.key">
                  <!-- Date/Time Column with Time Ago -->
                  <div v-if="column.key === 'transactionDate'" class="date-time-cell">
                    <div class="date-part">{{ formatDateTime(record.transactionDate) }}</div>
                    <div class="time-ago">{{ getTimeAgo(record.transactionDate) }}</div>
                  </div>
                  
                  <!-- Date Only Column -->
                  <span v-if="column.key === 'dateOnly'" class="date-cell">
                    {{ record.dateOnly || formatDateOnly(record.transactionDate) }}
                  </span>
                  
                  <!-- Time Column -->
                  <span v-if="column.key === 'time'" class="time-cell">
                    {{ record.time || formatTimeOnly(record.transactionDate) }}
                  </span>
                  
                  <!-- Year/Month/Day Columns -->
                  <span v-if="column.key === 'year' || column.key === 'month' || column.key === 'day'" class="metric-value">
                    {{ record[column.key] || 0 }}
                  </span>
                  
                  <!-- Transaction ID -->
                  <code v-if="column.key === 'unqTrID'" class="transaction-id colorful">
                    {{ record.unqTrID }}
                  </code>
                  
                  <!-- Other ID Columns -->
                  <code v-if="column.key === 'id' || column.key === 'recnumber' || (column.key.endsWith('ID') && column.key !== 'unqTrID')" class="id-code">
                    {{ record[column.key] || 'N/A' }}
                  </code>
                  
                  <!-- Store Column with colored badge -->
                  <span v-if="column.key === 'store'" :class="['store-badge', getStoreClass(record.store)]">
                    {{ record.store }}
                  </span>
                  
                  <!-- Device/Equipment Column -->
                  <div v-if="column.key === 'equipment'" class="equipment-cell">
                    <span class="equipment-name">{{ record.equipment || 'N/A' }}</span>
                  </div>
                  
                  <!-- Master Equipment -->
                  <span v-if="column.key === 'masterEquipment'" class="equipment-name">
                    {{ record.masterEquipment || 'N/A' }}
                  </span>
                  
                  <!-- Registration Column -->
                  <code v-if="column.key === 'registration'" class="registration-badge">
                    {{ record.registration }}
                  </code>
                  
                  <!-- Equipment Tag Columns -->
                  <code v-if="column.key === 'eqpTag' || column.key === 'equipmentTagfromField'" class="tag-code">
                    {{ record[column.key] || 'N/A' }}
                  </code>
                  
                  <!-- Product Column with colored badge -->
                  <span v-if="column.key === 'product'" :class="['product-badge-new', getProductClass(record.product)]">
                    {{ record.product }}
                  </span>
                  
                  <!-- Volume Column with colored text -->
                  <div v-if="column.key === 'volume'" class="volume-cell-new">
                    <span class="volume-value">{{ formatVolume(record.volume) }}</span>
                    <span class="volume-unit">L</span>
                  </div>
                  
                  <!-- Activity Type Column -->
                  <span v-if="column.key === 'activityType'" :class="['activity-type-badge', getActivityTypeClass(record)]">
                    {{ getActivityTypeText(record) }}
                  </span>
                  
                  <!-- Details Column -->
                  <div v-if="column.key === 'details'" class="details-cell-new">
                    <span class="details-text">
                      Finished @ {{ formatVolume(record.volume) }}L on Trid:{{ record.unqTrID }}
                    </span>
                  </div>
                  
                  <!-- Status Column with colored indicator -->
                  <div v-if="column.key === 'status'" class="status-cell-new">
                    <span class="status-dot-new processed"></span>
                    <span class="status-text-new">processed</span>
                  </div>
                  
                  <!-- Account Column -->
                  <span v-if="column.key === 'account'" class="account-name">
                    {{ record.account }}
                  </span>
                  
                  <!-- Cost Columns - Only show in general views, not aggregated -->
                  <div v-if="column.key === 'totalCost' && !filters.reportType.startsWith('usage-')" class="cost-primary">
                    ZAR {{ formatCurrency(record.totalCost) }}
                  </div>
                  
                  <!-- Fuel Price Column -->
                  <span v-if="column.key === 'fuelPrice'" class="price-value">
                    ZAR {{ formatCurrency(record.fuelPrice) }}/L
                  </span>
                  
                  <!-- Fuel Efficiency Column -->
                  <span v-if="column.key === 'fuelEfficiency'" class="metric-value">
                    {{ record.fuelEfficiency || 'N/A' }}
                  </span>
                  
                  <!-- Personnel Columns -->
                  <span v-if="column.key === 'driver' || column.key === 'fuelAttendant' || column.key === 'auth'" class="personnel-name">
                    {{ record[column.key] || 'Not Assigned' }}
                  </span>
                  
                  <!-- Cost Centre Columns -->
                  <span v-if="column.key === 'costCentre' || column.key === 'costCentreDesc'" class="cost-centre">
                    {{ record[column.key] || 'N/A' }}
                  </span>
                  
                  <!-- Allocation Columns -->
                  <span v-if="column.key === 'allocation' || column.key === 'allocation2' || column.key === 'allocation3' || column.key === 'allocation4'" class="allocation-text">
                    {{ record[column.key] || 'N/A' }}
                  </span>
                  
                  <!-- Job Number Column -->
                  <code v-if="column.key === 'jobNumber'" class="job-code">
                    {{ record.jobNumber || 'N/A' }}
                  </code>
                  
                  <!-- Metric Columns -->
                  <span v-if="column.key === 'km' || column.key === 'hour' || column.key === 'consumption' || column.key === 'totalizer' || column.key === 'totalizerEnd'" class="metric-value">
                    {{ record[column.key] || 0 }}
                  </span>
                  
                  <!-- Measurement Column -->
                  <span v-if="column.key === 'measurement'" class="measurement-text">
                    {{ record.measurement || 'None' }}
                  </span>
                  
                  <!-- Description Column -->
                  <span v-if="column.key === 'description'" class="description-text">
                    {{ record.description || 'N/A' }}
                  </span>
                  
                  <!-- Rebate Column -->
                  <span v-if="column.key === 'rebate'" class="rebate-text">
                    {{ record.rebate || 'N/A' }}
                  </span>
                  
                  <!-- Information Columns -->
                  <code v-if="column.key.startsWith('information')" class="info-code">
                    {{ record[column.key] || 'N/A' }}
                  </code>
                  
                  <!-- Registration Column (for equipment aggregated view) -->
                  <span v-if="column.key === 'registration' && filters.reportType === 'usage-equipment'" class="registration-badge">
                    {{ getCleanRegistration(record.registration) }}
                  </span>
                  
                  <!-- Equipment Tag Column (for equipment aggregated view) -->
                  <code v-if="column.key === 'equipmentTag' && filters.reportType === 'usage-equipment'" class="tag-code">
                    {{ record.equipmentTag || 'N/A' }}
                  </code>
                  
                  <!-- Group Name (for aggregated views) -->
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-product'" :class="['product-badge-new', getProductClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-store'" :class="['store-badge', getStoreClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-master-group'" :class="['master-group-badge', getMasterGroupClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-allocations'" :class="['allocation-badge', getAllocationClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-cost-centres'" :class="['cost-centre-badge', getCostCentreClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && (filters.reportType === 'usage-driver' || filters.reportType === 'usage-fuel-attendant')" :class="['employee-badge', getEmployeeClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && filters.reportType === 'usage-equipment'" :class="['equipment-badge', getEquipmentClass(record.groupName)]">
                    {{ record.groupName }}
                  </span>
                  <span v-if="column.key === 'groupName' && !filters.reportType.startsWith('usage-')" class="group-name">
                    {{ record.groupName }}
                  </span>
                  
                  <!-- Transaction Count (for aggregated views) -->
                  <span v-if="column.key === 'transactionCount'" class="count-value">
                    {{ record.transactionCount }}
                  </span>
                  
                  <!-- Average Volume (for aggregated views) -->
                  <div v-if="column.key === 'avgVolume'" class="volume-primary">
                    {{ formatVolume(record.avgVolume) }} L
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          
          <div v-if="!loading && displayData.length === 0" class="empty-state">
            <div class="empty-icon">⛽</div>
            <div class="empty-title">No Records Found</div>
            <div class="empty-description">
              Try adjusting your filters or check back later for new data
            </div>
          </div>
        </div>
        
        <div v-if="totalPages > 1" class="pagination-container">
          <div class="pagination-info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to 
            {{ Math.min(currentPage * itemsPerPage, displayData.length) }} 
            of {{ displayData.length }} entries
          </div>
          <div class="pagination-controls">
            <button 
              class="page-btn prev-btn" 
              @click="currentPage = 1"
              :disabled="currentPage === 1">
              First
            </button>
            <button 
              class="page-btn prev-btn" 
              @click="currentPage--"
              :disabled="currentPage === 1">
              Previous
            </button>
            <button 
              v-for="page in visiblePageNumbers" 
              :key="page"
              class="page-btn page-number"
              :class="{ 'active': page === currentPage }"
              @click="currentPage = page">
              {{ page }}
            </button>
            <button 
              class="page-btn next-btn" 
              @click="currentPage++"
              :disabled="currentPage === totalPages">
              Next
            </button>
            <button 
              class="page-btn next-btn" 
              @click="currentPage = totalPages"
              :disabled="currentPage === totalPages">
              Last
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FamsQuickDispenseView',

  data: function() {
    return {
      // Popup states
      selectedRecord: null,
      
      // Drill-down popup states  
      showDrillDown: false,
      drillDownTitle: '',
      drillDownGroup: '',
      drillDownData: {
        groupName: '',
        records: [],
        totalVolume: 0,
        totalCost: 0
      },
      
      // Draggable popup position
      drillDownPosition: { x: 0, y: 0 },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      
      // Auto-refresh settings
      autoRefreshEnabled: false,
      refreshInterval: 60000,
      refreshTimer: null,
      
      // Loading states
      loading: false,

      // API Configuration
      apiConfig: {
        baseURL: process.env.VUE_APP_API_URL || '',
        accountId: localStorage.getItem('userAccountId')
      },

      // Pagination
      currentPage: 1,
      itemsPerPage: 25,

      // Main data
      records: [],
      
      // Summary statistics
      summaryStats: {
        totalTransactions: 0,
        totalVolume: 0,
        totalCost: 0,
        uniqueEquipment: 0,
        uniqueProducts: 0,
        avgVolume: 0
      },

      // Filters
      filters: {
      fromDate: this.getDefaultFromDate(),
      toDate: this.getDefaultToDate(),
      reportType: 'general',
      productIds: [],        // Changed from productId to productIds (array)
      storeIds: [],          // Changed from storeId to storeIds (array)
      equipmentIds: [],      // Changed from equipmentId to equipmentIds (array)
      masterEquipmentIds: [], // Changed from masterEquipmentId to masterEquipmentIds (array)
      allocation1Ids: [],    // Changed from allocation1Id to allocation1Ids (array)
      costCentreIds: []      // Changed from costCentreId to costCentreIds (array)
    },
      filtersVisible: true,
      statsVisible: true,
      
      // Dropdown states
      dropdownStates: {
      columns: false,
      products: false,
      stores: false,
      equipment: false,
      masterEquipment: false,
      allocations: false,
      costCentres: false
    },
      
      // Unique values for filters
      uniqueStores: [],
      uniqueProducts: [],
      uniqueEquipment: [],
      uniqueMasterEquipment: [],
      uniqueAllocations1: [],
      uniqueCostCentres: [],
      
      // Column visibility management based on report type
      availableColumns: [],
      selectedColumns: [],
      
      // Column search filters for general view
      columnSearchFilters: {},
      
      // All possible columns
      allColumns: {
        general: [
          { key: 'transactionDate', label: 'Date & Time', visible: true, order: 1 },
          { key: 'dateOnly', label: 'Date Only', visible: false, order: 2 },
          { key: 'time', label: 'Time', visible: false, order: 3 },
          { key: 'unqTrID', label: 'Transaction ID', visible: true, order: 4 },
          { key: 'id', label: 'ID', visible: false, order: 5 },
          { key: 'recnumber', label: 'Record Number', visible: false, order: 6 },
          { key: 'store', label: 'Store', visible: true, order: 7 },
          { key: 'storeID', label: 'Store ID', visible: false, order: 8 },
          { key: 'storeTypeID', label: 'Store Type ID', visible: false, order: 9 },
          { key: 'equipment', label: 'Equipment', visible: true, order: 10 },
          { key: 'equipmentID', label: 'Equipment ID', visible: false, order: 11 },
          { key: 'registration', label: 'Registration', visible: true, order: 12 },
          { key: 'eqpTag', label: 'Equipment Tag', visible: false, order: 13 },
          { key: 'equipmentTagfromField', label: 'Equipment Tag From Field', visible: false, order: 14 },
          { key: 'masterEquipment', label: 'Master Equipment', visible: false, order: 15 },
          { key: 'masterEquipmentID', label: 'Master Equipment ID', visible: false, order: 16 },
          { key: 'product', label: 'Product', visible: true, order: 17 },
          { key: 'productID', label: 'Product ID', visible: false, order: 18 },
          { key: 'volume', label: 'Volume (L)', visible: true, order: 19 },
          { key: 'totalCost', label: 'Total Cost', visible: false, order: 20 },
          { key: 'fuelPrice', label: 'Fuel Price', visible: false, order: 21 },
          { key: 'fuelEfficiency', label: 'Fuel Efficiency', visible: false, order: 22 },
          { key: 'driver', label: 'Driver', visible: false, order: 23 },
          { key: 'driverID', label: 'Driver ID', visible: false, order: 24 },
          { key: 'fuelAttendant', label: 'Fuel Attendant', visible: false, order: 25 },
          { key: 'operatorID', label: 'Operator ID', visible: false, order: 26 },
          { key: 'auth', label: 'Authorization', visible: false, order: 27 },
          { key: 'authorizationID', label: 'Authorization ID', visible: false, order: 28 },
          { key: 'costCentre', label: 'Cost Centre', visible: false, order: 29 },
          { key: 'costCentreID', label: 'Cost Centre ID', visible: false, order: 30 },
          { key: 'costCentreDesc', label: 'Cost Centre Description', visible: false, order: 31 },
          { key: 'jobNumber', label: 'Job Number', visible: false, order: 32 },
          { key: 'km', label: 'KM', visible: false, order: 33 },
          { key: 'hour', label: 'Hour', visible: false, order: 34 },
          { key: 'consumption', label: 'Consumption', visible: false, order: 35 },
          { key: 'allocation', label: 'Allocation', visible: false, order: 36 },
          { key: 'allocationID', label: 'Allocation ID', visible: false, order: 37 },
          { key: 'allocation2', label: 'Allocation 2', visible: false, order: 38 },
          { key: 'allocationID2', label: 'Allocation ID 2', visible: false, order: 39 },
          { key: 'allocation3', label: 'Allocation 3', visible: false, order: 40 },
          { key: 'allocationID3', label: 'Allocation ID 3', visible: false, order: 41 },
          { key: 'allocation4', label: 'Allocation 4', visible: false, order: 42 },
          { key: 'allocationID4', label: 'Allocation ID 4', visible: false, order: 43 },
          { key: 'measurement', label: 'Measurement', visible: false, order: 44 },
          { key: 'measurementID', label: 'Measurement ID', visible: false, order: 45 },
          { key: 'description', label: 'Description', visible: false, order: 46 },
          { key: 'rebate', label: 'Rebate', visible: false, order: 47 },
          { key: 'rebateID', label: 'Rebate ID', visible: false, order: 48 },
          { key: 'totalizer', label: 'Totalizer', visible: false, order: 49 },
          { key: 'totalizerEnd', label: 'Totalizer End', visible: false, order: 50 },
          { key: 'year', label: 'Year', visible: false, order: 51 },
          { key: 'month', label: 'Month', visible: false, order: 52 },
          { key: 'day', label: 'Day', visible: false, order: 53 },
          { key: 'account', label: 'Account', visible: false, order: 54 },
          { key: 'accountID', label: 'Account ID', visible: false, order: 55 },
          { key: 'recordTypeID', label: 'Record Type ID', visible: false, order: 56 },
          { key: 'informationReg', label: 'Information Reg', visible: false, order: 57 },
          { key: 'informationOpr', label: 'Information Opr', visible: false, order: 58 },
          { key: 'informationDrv', label: 'Information Drv', visible: false, order: 59 },
          { key: 'informationAth', label: 'Information Ath', visible: false, order: 60 },
          { key: 'informationAllocation', label: 'Information Allocation', visible: false, order: 61 },
          { key: 'informationMac', label: 'Information Mac', visible: false, order: 62 }
        ],
        aggregated: [
          { key: 'groupName', label: 'Name', visible: true, order: 1 },
          { key: 'transactionCount', label: 'Transactions', visible: true, order: 2 },
          { key: 'volume', label: 'Total Volume (L)', visible: true, order: 3 },
          { key: 'totalCost', label: 'Total Cost', visible: false, order: 4 },
          { key: 'avgVolume', label: 'Avg Volume (L)', visible: true, order: 5 },
          { key: 'avgCost', label: 'Avg Cost', visible: false, order: 6 }
        ]
      }
    };
  },

  computed: {
    visibleColumns: function() {
      var self = this;
      var isAggregatedReport = this.filters.reportType.startsWith('usage-');
      
      return this.availableColumns
        .filter(function(col) {
          // Always exclude cost columns from aggregated reports
          if (isAggregatedReport && (col.key === 'totalCost' || col.key === 'avgCost')) {
            return false;
          }
          return self.selectedColumns.indexOf(col.key) !== -1;
        })
        .sort(function(a, b) {
          return a.order - b.order;
        });
    },
    
    hasActiveColumnSearches: function() {
      var self = this;
      if (!this.columnSearchFilters) return false;
      
      return Object.keys(this.columnSearchFilters).some(function(key) {
        return self.columnSearchFilters[key] && self.columnSearchFilters[key].trim() !== '';
      });
    },
    
    displayData: function() {
      // Return aggregated or filtered data based on report type
      if (this.filters.reportType === 'general' || this.filters.reportType === 'general-price') {
        return this.filteredRecords;
      } else {
        return this.aggregateDataByReportType();
      }
    },
    
    filteredRecordsOld: function() {
      var filtered = this.records.slice();
      var self = this;
      
      // Product filter
      if (this.filters.productId) {
        filtered = filtered.filter(function(r) {
          return r.productID == self.filters.productId;
        });
      }
      
      // Store filter
      if (this.filters.storeId) {
        filtered = filtered.filter(function(r) {
          return r.storeID == self.filters.storeId;
        });
      }
      
      // Equipment filter
      if (this.filters.equipmentId) {
        filtered = filtered.filter(function(r) {
          return r.equipmentID == self.filters.equipmentId;
        });
      }
      
      // Master Equipment filter
      if (this.filters.masterEquipmentId) {
        filtered = filtered.filter(function(r) {
          return r.masterEquipmentID == self.filters.masterEquipmentId;
        });
      }
      
      // Allocation1 filter
      if (this.filters.allocation1Id) {
        filtered = filtered.filter(function(r) {
          return r.allocationID == self.filters.allocation1Id;
        });
      }
      
      // Cost Centre filter
      if (this.filters.costCentreId) {
        filtered = filtered.filter(function(r) {
          return r.costCentreID == self.filters.costCentreId;
        });
      }
      
      // Apply column search filters
      if (this.columnSearchFilters && Object.keys(this.columnSearchFilters).length > 0) {
        filtered = filtered.filter(function(record) {
          for (var key in self.columnSearchFilters) {
            if (self.columnSearchFilters[key] && self.columnSearchFilters[key].trim() !== '') {
              var searchValue = self.columnSearchFilters[key].toLowerCase();
              var recordValue = '';
              
              // Get the record value based on the column key
              if (key === 'transactionDate') {
                recordValue = self.formatDateTime(record[key]);
              } else if (key === 'volume' || key === 'avgVolume') {
                recordValue = self.formatVolume(record[key]);
              } else if (key === 'totalCost' || key === 'avgCost' || key === 'fuelPrice') {
                recordValue = self.formatCurrency(record[key]);
              } else {
                recordValue = String(record[key] || '');
              }
              
              // Check if the record value contains the search value
              if (!recordValue.toLowerCase().includes(searchValue)) {
                return false;
              }
            }
          }
          return true;
        });
      }
      
      return filtered;
    },

filteredRecords: function() {
  var filtered = this.records.slice();
  var self = this;
  
  // Product filter - now handles multiple selections
  if (this.filters.productIds && this.filters.productIds.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.productIds.indexOf(String(r.productID)) !== -1;
    });
  }
  
  // Store filter - now handles multiple selections
  if (this.filters.storeIds && this.filters.storeIds.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.storeIds.indexOf(String(r.storeID)) !== -1;
    });
  }
  
  // Equipment filter - now handles multiple selections
  if (this.filters.equipmentIds && this.filters.equipmentIds.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.equipmentIds.indexOf(String(r.equipmentID)) !== -1;
    });
  }
  
  // Master Equipment filter - now handles multiple selections
  if (this.filters.masterEquipmentIds && this.filters.masterEquipmentIds.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.masterEquipmentIds.indexOf(String(r.masterEquipmentID)) !== -1;
    });
  }
  
  // Allocation1 filter - now handles multiple selections
  if (this.filters.allocation1Ids && this.filters.allocation1Ids.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.allocation1Ids.indexOf(String(r.allocationID)) !== -1;
    });
  }
  
  // Cost Centre filter - now handles multiple selections
  if (this.filters.costCentreIds && this.filters.costCentreIds.length > 0) {
    filtered = filtered.filter(function(r) {
      return self.filters.costCentreIds.indexOf(String(r.costCentreID)) !== -1;
    });
  }
  
  // Apply column search filters - IMPORTANT: This needs to stay!
  if (this.columnSearchFilters && Object.keys(this.columnSearchFilters).length > 0) {
    filtered = filtered.filter(function(record) {
      for (var key in self.columnSearchFilters) {
        if (self.columnSearchFilters[key] && self.columnSearchFilters[key].trim() !== '') {
          var searchValue = self.columnSearchFilters[key].toLowerCase();
          var recordValue = '';
          
          // Get the record value based on the column key
          if (key === 'transactionDate') {
            recordValue = self.formatDateTime(record[key]);
          } else if (key === 'volume' || key === 'avgVolume') {
            // For volume, search the actual number value
            recordValue = String(record[key] || '0');
          } else if (key === 'totalCost' || key === 'avgCost' || key === 'fuelPrice') {
            recordValue = String(record[key] || '0');
          } else {
            recordValue = String(record[key] || '');
          }
          
          // Check if the record value contains the search value
          if (!recordValue.toLowerCase().includes(searchValue)) {
            return false;
          }
        }
      }
      return true;
    });
  }
  
  return filtered;
},

    totalPages: function() {
      return Math.ceil(this.displayData.length / this.itemsPerPage);
    },

    paginatedRecords: function() {
      var start = (this.currentPage - 1) * this.itemsPerPage;
      var end = start + this.itemsPerPage;
      return this.displayData.slice(start, end);
    },
    
    visiblePageNumbers: function() {
      var maxVisible = 5;
      var pages = [];
      var start = Math.max(1, this.currentPage - 2);
      var end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (var i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    }
  },

  methods: {
    getDefaultFromDate3Days: function() {
      var date = new Date();
      date.setDate(date.getDate() - 3);
      return this.formatDateForInput(date);
    },
    
    getDefaultFromDate: function () {
      var date = new Date();
      date.setDate(1);         // Set to 1st day of this month
      return this.formatDateForInput(date);
    },

    getDefaultToDate: function() {
      var date = new Date();
      return this.formatDateForInput(date);
    },
    
    formatDateForInput: function(date) {
      var year = date.getFullYear();
      var month = String(date.getMonth() + 1).padStart(2, '0');
      var day = String(date.getDate()).padStart(2, '0');
      
      return year + '-' + month + '-' + day;
    },

    loadComparisonData: function() {
      var self = this;
      
      self.loading = true;

      // Format dates for API (YYYY-MM-DD format)
      var fromDate = self.filters.fromDate;
      var toDate = self.filters.toDate;

      var onSuccess = function(response) {
        console.log('API Response received:', response);
        
        // Direct data assignment from the response
        if (response.data && Array.isArray(response.data)) {
          self.records = response.data;
          self.calculateSummaryStats();
          self.extractUniqueValues();
          self.updateAvailableColumns();
          
          self.showNotification('Success', 'Loaded ' + self.records.length + ' quick dispensing records', 'success');
        } else {
          console.error('Invalid data format in response');
          self.records = [];
        }
      };

      var onError = function(error) {
        console.error('API Error:', error);
        self.loading = false;
        self.showNotification('Error', 'Failed to load data: ' + (error.message || 'Unknown error'), 'danger');
      };

      var onFinally = function() {
        console.log('Data load attempt finished.');
        self.loading = false;
      };

      // Build the API URL with parameters
      var apiUrl = 'VwIotdataNotificationComprehensiveActivity/GetQuickDispensingInfo?accountId=' + self.apiConfig.accountId;
      
      if (fromDate) {
        apiUrl += '&Fromdate=' + fromDate;
      }
      
      if (toDate) {
        apiUrl += '&Todate=' + toDate;
      }

      console.log('Calling API:', apiUrl);

      // API call with new endpoint
      self.$ajaxGet(
        self,
        apiUrl,
        onSuccess,
        onFinally,
        onError
      );
    },
    
    calculateSummaryStats: function() {
      var stats = {
        totalTransactions: this.records.length,
        totalVolume: 0,
        totalCost: 0,
        uniqueEquipment: new Set(),
        uniqueProducts: new Set(),
        avgVolume: 0
      };
      
      this.records.forEach(function(record) {
        stats.totalVolume += record.volume || 0;
        stats.totalCost += record.totalCost || 0;
        
        if (record.equipmentID) {
          stats.uniqueEquipment.add(record.equipmentID);
        }
        
        if (record.productID) {
          stats.uniqueProducts.add(record.productID);
        }
      });
      
      stats.uniqueEquipment = stats.uniqueEquipment.size;
      stats.uniqueProducts = stats.uniqueProducts.size;
      
      if (stats.totalTransactions > 0) {
        stats.avgVolume = stats.totalVolume / stats.totalTransactions;
      }
      
      this.summaryStats = stats;
    },
    
    extractUniqueValues: function() {
      var stores = {};
      var products = {};
      var equipment = {};
      var masterEquipment = {};
      var allocations1 = {};
      var costCentres = {};
      
      this.records.forEach(function(record) {
        // Collect unique stores
        if (record.storeID && record.store) {
          stores[record.storeID] = record.store;
        }
        
        // Collect unique products
        if (record.productID && record.product) {
          products[record.productID] = record.product;
        }
        
        // Collect unique equipment
        if (record.equipmentID && record.equipment) {
          equipment[record.equipmentID] = record.equipment;
        }
        
        // Collect unique master equipment
        if (record.masterEquipmentID && record.masterEquipment) {
          masterEquipment[record.masterEquipmentID] = record.masterEquipment;
        }
        
        // Collect unique allocations (allocation1)
        if (record.allocationID && record.allocation) {
          allocations1[record.allocationID] = record.allocation;
        }
        
        // Collect unique cost centres
        if (record.costCentreID && record.costCentre) {
          costCentres[record.costCentreID] = record.costCentre;
        }
      });
      
      // Convert to arrays
      this.uniqueStores = Object.keys(stores).map(function(id) {
        return { id: id, name: stores[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      
      this.uniqueProducts = Object.keys(products).map(function(id) {
        return { id: id, name: products[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      
      this.uniqueEquipment = Object.keys(equipment).map(function(id) {
        return { id: id, name: equipment[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      
      this.uniqueMasterEquipment = Object.keys(masterEquipment).map(function(id) {
        return { id: id, name: masterEquipment[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      
      this.uniqueAllocations1 = Object.keys(allocations1).map(function(id) {
        return { id: id, name: allocations1[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
      
      this.uniqueCostCentres = Object.keys(costCentres).map(function(id) {
        return { id: id, name: costCentres[id] };
      }).sort(function(a, b) {
        return a.name.localeCompare(b.name);
      });
    },
    
    updateAvailableColumns: function() {
      var reportType = this.filters.reportType;
      
      if (reportType === 'general' || reportType === 'general-price') {
        this.availableColumns = this.allColumns.general.slice();
        
        // For general-price, make sure price columns are visible
        if (reportType === 'general-price') {
          this.selectedColumns = ['transactionDate', 'unqTrID', 'store', 'equipment', 'product', 'volume', 'totalCost', 'fuelPrice'];
        } else {
          this.selectedColumns = ['transactionDate', 'unqTrID', 'store', 'equipment', 'registration', 'product', 'volume'];
        }
      } else if (reportType === 'usage-equipment') {
        // Special columns for equipment aggregated view - Registration first, then Name, then Equipment Tag
        this.availableColumns = [
          { key: 'registration', label: 'Registration', visible: true, order: 1 },
          { key: 'groupName', label: 'Name', visible: true, order: 2 },
          { key: 'equipmentTag', label: 'Equipment Tag', visible: true, order: 3 },
          { key: 'transactionCount', label: 'Transactions', visible: true, order: 4 },
          { key: 'volume', label: 'Total Volume (L)', visible: true, order: 5 },
          { key: 'totalCost', label: 'Total Cost', visible: false, order: 6 },
          { key: 'avgVolume', label: 'Avg Volume (L)', visible: true, order: 7 },
          { key: 'avgCost', label: 'Avg Cost', visible: false, order: 8 }
        ];
        this.selectedColumns = this.availableColumns
          .filter(function(col) { return col.visible; })
          .map(function(col) { return col.key; });
      } else {
        // Other aggregated views
        this.availableColumns = this.allColumns.aggregated.slice();
        this.selectedColumns = this.allColumns.aggregated.map(function(col) {
          return col.key;
        });
      }
    },
    
    aggregateDataByReportType: function() {
      var self = this;
      var aggregated = {};
      var reportType = this.filters.reportType;
      
      this.filteredRecords.forEach(function(record) {
        var key = '';
        var name = '';
        
        switch(reportType) {
          case 'usage-product':
            key = record.productID;
            name = record.product;
            break;
          case 'usage-store':
            key = record.storeID;
            name = record.store;
            break;
          case 'usage-equipment':
            key = record.equipmentID;
            name = record.equipment;
            console.log('Processing equipment:', name, 'with ID:', key, 'and registration:', record.registration);
            break;
          case 'usage-master-group':
            key = record.masterEquipmentID || 'none';
            name = record.masterEquipment || 'No Master Group';
            break;
          case 'usage-driver':
            key = record.driverID || 'unassigned';
            name = record.driver || 'Not Assigned';
            break;
          case 'usage-fuel-attendant':
            key = record.operatorID || 'unassigned';
            name = record.fuelAttendant || 'Not Assigned';
            break;
          case 'usage-cost-centres':
            key = record.costCentreID || 'none';
            name = record.costCentre || 'No Cost Centre';
            break;
          case 'usage-allocations':
            key = record.allocationID || 'none';
            name = record.allocation || 'No Allocation';
            break;
          default:
            key = 'all';
            name = 'All Records';
        }
        
        if (!aggregated[key]) {
          aggregated[key] = {
            groupKey: key,
            groupName: name,
            transactionCount: 0,
            volume: 0,
            totalCost: 0,
            records: []
          };
          
          // Add registration and equipment tag fields for equipment reports  
          if (reportType === 'usage-equipment') {
            // Store only the first registration found - clean any duplicates
            if (record.registration && !aggregated[key].registration) {
              // Clean the registration before storing
              var cleanReg = record.registration;
              var parts = String(cleanReg).trim().split(/\s+/);
              
              // Check for simple duplication
              if (parts.length === 2 && parts[0] === parts[1]) {
                cleanReg = parts[0];
                console.log('Cleaned duplicate registration from "' + record.registration + '" to "' + cleanReg + '"');
              } else if (parts.length > 2) {
                // Check for multi-word duplication
                var halfLen = Math.floor(parts.length / 2);
                if (parts.length % 2 === 0) {
                  var firstHalf = parts.slice(0, halfLen).join(' ');
                  var secondHalf = parts.slice(halfLen).join(' ');
                  if (firstHalf === secondHalf) {
                    cleanReg = firstHalf;
                    console.log('Cleaned duplicate registration from "' + record.registration + '" to "' + cleanReg + '"');
                  }
                }
              }
              
              aggregated[key].registration = cleanReg;
              console.log('Setting registration for equipment', name, ':', cleanReg);
            }
            if ((record.eqpTag || record.equipmentTagfromField) && !aggregated[key].equipmentTag) {
              aggregated[key].equipmentTag = record.eqpTag || record.equipmentTagfromField;
            }
          }
        } else {
          // Debug if registration is being updated
          if (reportType === 'usage-equipment' && record.registration && !aggregated[key].registration) {
            console.log('Update registration for', name, 'from empty to:', record.registration);
            aggregated[key].registration = record.registration;
          }
        }
        
        aggregated[key].transactionCount++;
        aggregated[key].volume += record.volume || 0;
        aggregated[key].totalCost += record.totalCost || 0;
        aggregated[key].records.push(record);
      });
      
      // Convert to array and calculate averages
      var result = Object.values(aggregated).map(function(group) {
        group.avgVolume = group.transactionCount > 0 ? group.volume / group.transactionCount : 0;
        group.avgCost = group.transactionCount > 0 ? group.totalCost / group.transactionCount : 0;
        
        // Log the final registration for equipment reports
        if (reportType === 'usage-equipment' && group.registration) {
          console.log('Final registration for', group.groupName, ':', group.registration);
        }
        
        return group;
      }).sort(function(a, b) {
        return b.volume - a.volume; // Sort by volume descending
      });
      
      return result;
    },
    
    getReportTitle: function() {
      var titles = {
        'general': 'Quick Dispensing Data - General View',
        'general-price': 'Quick Dispensing Data - General View with Price',
        'usage-product': 'Usage per Product',
        'usage-store': 'Usage per Store',
        'usage-equipment': 'Usage per Equipment',
        'usage-master-group': 'Usage per Master Group',
        'usage-driver': 'Usage per Driver',
        'usage-fuel-attendant': 'Usage per Fuel Attendant',
        'usage-cost-centres': 'Usage per Cost Centres',
        'usage-allocations': 'Usage per Allocations'
      };
      
      return titles[this.filters.reportType] || 'Quick Dispensing Data';
    },
    
    handleReportTypeChange: function() {
      this.updateAvailableColumns();
      this.currentPage = 1;
      // Clear column search filters when changing report type
      this.columnSearchFilters = {};
    },
    
    handleColumnSearch: function() {
      // Reset to first page when searching
      this.currentPage = 1;
      // The filtering happens automatically through the computed property
    },
    
    getProductClass: function(product) {
      if (product && product.toLowerCase().indexOf('diesel') !== -1) {
        return 'product-diesel';
      } else if (product && product.toLowerCase().indexOf('petrol') !== -1) {
        return 'product-petrol';
      } else if (product && product.toLowerCase().indexOf('gas') !== -1) {
        return 'product-gas';
      }
      return 'product-other';
    },
    
    getActivityTypeClass: function(record) {
      // You can customize this based on your business logic
      if (record.volume > 100) return 'activity-complete';
      if (record.volume > 50) return 'activity-partial';
      return 'activity-pending';
    },
    
    getActivityTypeText: function(record) {
      // You can customize this based on your business logic
      if (record.volume > 100) return 'COMPLETE';
      if (record.volume > 50) return 'PARTIAL';
      return 'PENDING';
    },
    
    getStoreClass: function(storeName) {
      if (!storeName) return 'store-default';
      
      // Generate consistent color based on store name
      var storeNameLower = storeName.toLowerCase();
      
      // Specific store patterns
      if (storeNameLower.includes('diesel')) return 'store-diesel';
      if (storeNameLower.includes('bowser')) return 'store-bowser';
      if (storeNameLower.includes('north')) return 'store-north';
      if (storeNameLower.includes('south')) return 'store-south';
      if (storeNameLower.includes('east')) return 'store-east';
      if (storeNameLower.includes('west')) return 'store-west';
      if (storeNameLower.includes('central')) return 'store-central';
      if (storeNameLower.includes('main')) return 'store-main';
      if (storeNameLower.includes('vgl')) return 'store-vgl';
      if (storeNameLower.includes('a4g')) return 'store-a4g';
      
      // Generate color based on first character for consistency
      var firstChar = storeName.charAt(0).toUpperCase();
      if (firstChar <= 'E') return 'store-type-1';
      if (firstChar <= 'J') return 'store-type-2';
      if (firstChar <= 'O') return 'store-type-3';
      if (firstChar <= 'T') return 'store-type-4';
      if (firstChar <= 'Z') return 'store-type-5';
      
      return 'store-default';
    },
    
    getMasterGroupClass: function(groupName) {
      if (!groupName) return 'master-group-default';
      
      var groupNameLower = groupName.toLowerCase();
      
      // Specific master group patterns
      if (groupNameLower.includes('transport')) return 'master-group-transport';
      if (groupNameLower.includes('production')) return 'master-group-production';
      if (groupNameLower.includes('workshop')) return 'master-group-workshop';
      if (groupNameLower.includes('odb') || groupNameLower.includes('operations')) return 'master-group-operations';
      if (groupNameLower.includes('maintenance')) return 'master-group-maintenance';
      if (groupNameLower.includes('admin')) return 'master-group-admin';
      if (groupNameLower.includes('fleet')) return 'master-group-fleet';
      if (groupNameLower.includes('logistics')) return 'master-group-logistics';
      
      // Generate color based on first character
      var firstChar = groupName.charAt(0).toUpperCase();
      if (firstChar <= 'F') return 'master-group-type-1';
      if (firstChar <= 'L') return 'master-group-type-2';
      if (firstChar <= 'R') return 'master-group-type-3';
      if (firstChar <= 'Z') return 'master-group-type-4';
      
      return 'master-group-default';
    },
    
    getAllocationClass: function(allocationName) {
      if (!allocationName) return 'allocation-default';
      
      var allocationLower = allocationName.toLowerCase();
      
      // Specific allocation patterns
      if (allocationLower.includes('project')) return 'allocation-project';
      if (allocationLower.includes('department')) return 'allocation-department';
      if (allocationLower.includes('division')) return 'allocation-division';
      if (allocationLower.includes('branch')) return 'allocation-branch';
      if (allocationLower.includes('region')) return 'allocation-region';
      if (allocationLower.includes('cost')) return 'allocation-cost';
      if (allocationLower.includes('budget')) return 'allocation-budget';
      if (allocationLower.includes('contract')) return 'allocation-contract';
      
      // Generate color based on first character
      var firstChar = allocationName.charAt(0).toUpperCase();
      if (firstChar <= 'G') return 'allocation-type-1';
      if (firstChar <= 'M') return 'allocation-type-2';
      if (firstChar <= 'S') return 'allocation-type-3';
      if (firstChar <= 'Z') return 'allocation-type-4';
      
      return 'allocation-default';
    },
    
    getCostCentreClass: function(costCentreName) {
      if (!costCentreName) return 'cost-centre-default';
      
      var ccLower = costCentreName.toLowerCase();
      
      // Specific cost centre patterns
      if (ccLower.includes('transport')) return 'cost-centre-transport';
      if (ccLower.includes('production')) return 'cost-centre-production';
      if (ccLower.includes('workshop')) return 'cost-centre-workshop';
      if (ccLower.includes('odb')) return 'cost-centre-odb';
      if (ccLower.includes('admin')) return 'cost-centre-admin';
      if (ccLower.includes('finance')) return 'cost-centre-finance';
      if (ccLower.includes('hr') || ccLower.includes('human')) return 'cost-centre-hr';
      if (ccLower.includes('it') || ccLower.includes('tech')) return 'cost-centre-it';
      
      // Generate color based on first character
      var firstChar = costCentreName.charAt(0).toUpperCase();
      if (firstChar <= 'H') return 'cost-centre-type-1';
      if (firstChar <= 'O') return 'cost-centre-type-2';
      if (firstChar <= 'T') return 'cost-centre-type-3';
      if (firstChar <= 'Z') return 'cost-centre-type-4';
      
      return 'cost-centre-default';
    },
    
    getEmployeeClass: function(employeeName) {
      if (!employeeName || employeeName === 'None') return 'employee-default';
      
      // Generate consistent color based on employee name for drivers/fuel attendants
      var firstChar = employeeName.charAt(0).toUpperCase();
      if (firstChar <= 'E') return 'employee-type-1';
      if (firstChar <= 'J') return 'employee-type-2';
      if (firstChar <= 'O') return 'employee-type-3';
      if (firstChar <= 'T') return 'employee-type-4';
      if (firstChar <= 'Z') return 'employee-type-5';
      
      return 'employee-default';
    },
    
    getEquipmentClass: function(equipmentName) {
      if (!equipmentName) return 'equipment-default';
      
      var equipmentLower = equipmentName.toLowerCase();
      
      // Specific equipment patterns
      if (equipmentLower.includes('truck')) return 'equipment-truck';
      if (equipmentLower.includes('excavator')) return 'equipment-excavator';
      if (equipmentLower.includes('loader')) return 'equipment-loader';
      if (equipmentLower.includes('dozer')) return 'equipment-dozer';
      if (equipmentLower.includes('crane')) return 'equipment-crane';
      if (equipmentLower.includes('forklift')) return 'equipment-forklift';
      if (equipmentLower.includes('generator')) return 'equipment-generator';
      if (equipmentLower.includes('compressor')) return 'equipment-compressor';
      if (equipmentLower.includes('vehicle')) return 'equipment-vehicle';
      if (equipmentLower.includes('master')) return 'equipment-master';
      
      // Generate color based on first character
      var firstChar = equipmentName.charAt(0).toUpperCase();
      if (firstChar <= 'F') return 'equipment-type-1';
      if (firstChar <= 'L') return 'equipment-type-2';
      if (firstChar <= 'R') return 'equipment-type-3';
      if (firstChar <= 'Z') return 'equipment-type-4';
      
      return 'equipment-default';
    },
    
    cleanDuplicateText: function(text) {
      if (!text) return text;
      
      // Convert to string and trim
      var str = String(text).trim();
      
      // Split by spaces
      var parts = str.split(/\s+/);
      
      // If we have exactly 2 identical parts, return just one
      if (parts.length === 2 && parts[0] === parts[1]) {
        return parts[0];
      }
      
      // Check if text repeats itself (handle multi-word duplicates)
      if (parts.length > 1 && parts.length % 2 === 0) {
        var halfLength = parts.length / 2;
        var firstHalf = parts.slice(0, halfLength).join(' ');
        var secondHalf = parts.slice(halfLength).join(' ');
        
        if (firstHalf === secondHalf) {
          return firstHalf;
        }
      }
      
      // Also check if the entire string is repeated with no space
      var halfLen = Math.floor(str.length / 2);
      if (str.length % 2 === 0) {
        var first = str.substring(0, halfLen);
        var second = str.substring(halfLen);
        if (first === second) {
          return first;
        }
      }
      
      return str;
    },
    
    getCleanRegistration: function(registration) {
      if (!registration) return 'N/A';
      // Apply cleaning to handle any duplication
      return this.cleanDuplicateText(registration);
    },
    
    getRowClass: function(record) {
      var classes = [];
      
      // Add product-based class
      if (record.product) {
        if (record.product.toLowerCase().indexOf('diesel') !== -1) {
          classes.push('row-diesel');
        } else if (record.product.toLowerCase().indexOf('petrol') !== -1) {
          classes.push('row-petrol');
        }
      }
      
      // Add high-volume class
      if (record.volume > 500) {
        classes.push('row-high-volume');
      }
      
      return classes.join(' ');
    },
    
    getStatusClass: function(status) {
      if (!status) return 'status-pending';
      var statusLower = status.toLowerCase();
      if (statusLower === 'approved' || statusLower === 'complete' || statusLower === 'success') {
        return 'status-approved';
      } else if (statusLower === 'rejected' || statusLower === 'failed' || statusLower === 'error') {
        return 'status-rejected';
      } else if (statusLower === 'pending' || statusLower === 'processing') {
        return 'status-pending';
      }
      return 'status-default';
    },
    
    formatVolume: function(volume) {
      if (volume === null || volume === undefined) return '0.00';
      return parseFloat(volume).toFixed(2);
    },
    
    formatCurrency: function(amount) {
      if (amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toFixed(2);
    },
    
    formatDateTime: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);
      
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      var hours = String(d.getHours()).padStart(2, '0');
      var minutes = String(d.getMinutes()).padStart(2, '0');
      
      return year + '/' + month + '/' + day + ' ' + hours + ':' + minutes;
    },
    
    formatDateOnly: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);
      
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      
      return year + '/' + month + '/' + day;
    },
    
    formatTimeOnly: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);
      
      var hours = String(d.getHours()).padStart(2, '0');
      var minutes = String(d.getMinutes()).padStart(2, '0');
      var seconds = String(d.getSeconds()).padStart(2, '0');
      
      return hours + ':' + minutes + ':' + seconds;
    },
    
    getTimeAgo: function(date) {
      if (!date) return '';
      var d = new Date(date);
      var now = new Date();
      var diff = Math.floor((now - d) / 1000); // difference in seconds
      
      if (diff < 60) return diff + ' seconds ago';
      if (diff < 3600) return Math.floor(diff / 60) + ' minutes ago';
      if (diff < 86400) return Math.floor(diff / 3600) + ' hours ago';
      return Math.floor(diff / 86400) + ' days ago';
    },
    
    formatDateForDisplay: function(date) {
      if (!date) return 'N/A';
      var d = new Date(date);
      
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      
      return year + '/' + month + '/' + day;
    },
    
    showRecordDetails: function(record) {
      // Check if this is an aggregated row (has groupKey) or individual record
      if (record.groupKey || record.groupName) {
        // This is an aggregated row - show drill-down
        this.showDrillDownDetails(record);
      } else if (record.id || record.unqTrID) {
        // This is an individual record - show details popup
        this.selectedRecord = record;
      }
    },
    
    showDrillDownDetails: function(aggregatedData) {
      var reportType = this.filters.reportType;
      var groupLabel = '';
      
      switch(reportType) {
        case 'usage-product':
          groupLabel = 'Product';
          break;
        case 'usage-store':
          groupLabel = 'Store';
          break;
        case 'usage-equipment':
          groupLabel = 'Equipment';
          break;
        case 'usage-master-group':
          groupLabel = 'Master Group';
          break;
        case 'usage-driver':
          groupLabel = 'Driver';
          break;
        case 'usage-fuel-attendant':
          groupLabel = 'Fuel Attendant';
          break;
        case 'usage-cost-centres':
          groupLabel = 'Cost Centre';
          break;
        case 'usage-allocations':
          groupLabel = 'Allocation';
          break;
        default:
          groupLabel = 'Group';
      }
      
      this.drillDownTitle = 'Drill-down: ' + aggregatedData.groupName;
      this.drillDownGroup = groupLabel;
      this.drillDownData = {
        groupName: aggregatedData.groupName,
        records: aggregatedData.records || [],
        totalVolume: aggregatedData.volume || 0,
        totalCost: aggregatedData.totalCost || 0
      };
      
      // Center the popup on screen
      this.drillDownPosition = {
        x: (window.innerWidth - 1200) / 2, // 1200 is approx popup width
        y: 50 // Start 50px from top
      };
      
      this.showDrillDown = true;
    },
    
    closeDrillDown: function() {
      this.showDrillDown = false;
      this.drillDownData = {
        groupName: '',
        records: [],
        totalVolume: 0,
        totalCost: 0
      };
      this.isDragging = false;
    },
    
    startDrag: function(e) {
      this.isDragging = true;
      
      var clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      var clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      
      this.dragStart = {
        x: clientX - this.drillDownPosition.x,
        y: clientY - this.drillDownPosition.y
      };
      
      // Add event listeners
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.onDrag);
      document.addEventListener('touchend', this.stopDrag);
      
      // Prevent text selection while dragging
      e.preventDefault();
    },
    
    onDrag: function(e) {
      if (!this.isDragging) return;
      
      var clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      var clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
      
      // Calculate new position
      var newX = clientX - this.dragStart.x;
      var newY = clientY - this.dragStart.y;
      
      // Keep popup within viewport bounds
      var maxX = window.innerWidth - 1200; // Approximate popup width
      var maxY = window.innerHeight - 600; // Approximate popup height
      
      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));
      
      this.drillDownPosition = {
        x: newX,
        y: newY
      };
    },
    
    stopDrag: function() {
      this.isDragging = false;
      
      // Remove event listeners
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.stopDrag);
    },
    
    exportDrillDownExcel: function() {
      // Excel export functionality
      var self = this;
      
      // Create workbook
      var csv = 'Product,Store,Registration,Date,Driver,Fuel Attendant,Type,KM/Hour,Volume\n';
      
      this.drillDownData.records.forEach(function(record) {
        csv += [
          record.product,
          record.store,
          record.registration,
          self.formatDateTime(record.transactionDate),
          record.driver || 'None',
          record.fuelAttendant || 'None',
          record.measurement || 'None',
          record.km || record.hour || '0',
          self.formatVolume(record.volume)
        ].map(function(val) {
          return '"' + (val || '') + '"';
        }).join(',') + '\n';
      });
      
      // Add total row
      csv += '\n,,,,,,Total Volume:,,' + this.formatVolume(this.drillDownData.totalVolume) + '\n';
      
      var blob = new Blob([csv], { type: 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'drill_down_' + this.drillDownData.groupName.replace(/\s+/g, '_') + '_' + new Date().getTime() + '.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      
      this.showNotification('Success', 'Excel data exported successfully', 'success');
    },
    
    exportDrillDownCSV: function() {
      // CSV export functionality - same as Excel for simplicity
      this.exportDrillDownExcel();
    },
    
    printDrillDown: function() {
      var self = this;
      var printWindow = window.open('', '_blank', 'width=800,height=600');
      
      var html = '<!DOCTYPE html><html><head>';
      html += '<title>' + this.drillDownTitle + '</title>';
      html += '<style>';
      html += 'body { font-family: Arial, sans-serif; margin: 20px; }';
      html += 'h1 { color: #333; font-size: 1.5rem; }';
      html += 'h2 { color: #666; font-size: 1.2rem; margin-top: 10px; }';
      html += 'table { width: 100%; border-collapse: collapse; margin-top: 20px; }';
      html += 'th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }';
      html += 'th { background: #f0f0f0; font-weight: bold; }';
      html += '.total-row { font-weight: bold; background: #f8f8f8; }';
      html += '@media print { body { margin: 10mm; } }';
      html += '</style>';
      html += '</head><body>';
      html += '<h1>' + this.drillDownGroup + ': ' + this.drillDownData.groupName + '</h1>';
      html += '<h2>Volume: ' + this.formatVolume(this.drillDownData.totalVolume) + ' L</h2>';
      html += '<table>';
      html += '<thead><tr>';
      html += '<th>Product</th><th>Store</th><th>Registration</th><th>Date</th>';
      html += '<th>Driver</th><th>Fuel Attendant</th><th>Type</th><th>KM/Hour</th><th>Volume</th>';
      html += '</tr></thead><tbody>';
      
      this.drillDownData.records.forEach(function(record) {
        html += '<tr>';
        html += '<td>' + record.product + '</td>';
        html += '<td>' + record.store + '</td>';
        html += '<td>' + record.registration + '</td>';
        html += '<td>' + self.formatDateTime(record.transactionDate) + '</td>';
        html += '<td>' + (record.driver || 'None') + '</td>';
        html += '<td>' + (record.fuelAttendant || 'None') + '</td>';
        html += '<td>' + (record.measurement || 'None') + '</td>';
        html += '<td>' + (record.km || record.hour || '0') + '</td>';
        html += '<td>' + self.formatVolume(record.volume) + '</td>';
        html += '</tr>';
      });
      
      html += '<tr class="total-row">';
      html += '<td colspan="8">Total Volume:</td>';
      html += '<td>' + this.formatVolume(this.drillDownData.totalVolume) + '</td>';
      html += '</tr>';
      
      html += '</tbody></table>';
      html += '</body></html>';
      
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
      
      this.showNotification('Success', 'Print dialog opened', 'success');
    },
    
   // Update clearAllFilters method
    clearAllFilters: function() {
      this.filters.productIds = [];
      this.filters.storeIds = [];
      this.filters.equipmentIds = [];
      this.filters.masterEquipmentIds = [];
      this.filters.allocation1Ids = [];
      this.filters.costCentreIds = [];
      this.currentPage = 1;
    },
    
    toggleDropdown: function(type) {
      var self = this;
      Object.keys(this.dropdownStates).forEach(function(key) {
        if (key !== type) {
          self.dropdownStates[key] = false;
        }
      });
      this.dropdownStates[type] = !this.dropdownStates[type];
    },

      // Add these new toggle methods for multi-selects
  toggleAllProducts: function() {
    if (this.filters.productIds.length === this.uniqueProducts.length) {
      this.filters.productIds = [];
    } else {
      this.filters.productIds = this.uniqueProducts.map(function(p) { return p.id; });
    }
    this.applyFilters();
  },
  
  toggleAllStores: function() {
    if (this.filters.storeIds.length === this.uniqueStores.length) {
      this.filters.storeIds = [];
    } else {
      this.filters.storeIds = this.uniqueStores.map(function(s) { return s.id; });
    }
    this.applyFilters();
  },
  
  toggleAllEquipment: function() {
    if (this.filters.equipmentIds.length === this.uniqueEquipment.length) {
      this.filters.equipmentIds = [];
    } else {
      this.filters.equipmentIds = this.uniqueEquipment.map(function(e) { return e.id; });
    }
    this.applyFilters();
  },
  
  toggleAllMasterEquipment: function() {
    if (this.filters.masterEquipmentIds.length === this.uniqueMasterEquipment.length) {
      this.filters.masterEquipmentIds = [];
    } else {
      this.filters.masterEquipmentIds = this.uniqueMasterEquipment.map(function(me) { return me.id; });
    }
    this.applyFilters();
  },
  
  toggleAllAllocations: function() {
    if (this.filters.allocation1Ids.length === this.uniqueAllocations1.length) {
      this.filters.allocation1Ids = [];
    } else {
      this.filters.allocation1Ids = this.uniqueAllocations1.map(function(a) { return a.id; });
    }
    this.applyFilters();
  },
  
  toggleAllCostCentres: function() {
    if (this.filters.costCentreIds.length === this.uniqueCostCentres.length) {
      this.filters.costCentreIds = [];
    } else {
      this.filters.costCentreIds = this.uniqueCostCentres.map(function(cc) { return cc.id; });
    }
    this.applyFilters();
  },
  
    
    toggleAllColumns: function() {
      if (this.selectedColumns.length === this.availableColumns.length) {
        // Keep essential columns when deselecting all
        this.selectedColumns = this.availableColumns.slice(0, 3).map(function(col) {
          return col.key;
        });
      } else {
        this.selectedColumns = this.availableColumns.map(function(col) {
          return col.key;
        });
      }
    },
    
    handleColumnToggle: function() {
      // Ensure at least one column is always selected
      if (this.selectedColumns.length === 0) {
        this.selectedColumns = [this.availableColumns[0].key];
      }
    },
    
    applyFilters: function() {
      this.currentPage = 1;
      // Reload data with new date filters
      this.loadComparisonData();
    },
    
    // Update the resetFilters method
  resetFilters: function() {
    this.filters = {
      fromDate: this.getDefaultFromDate(),
      toDate: this.getDefaultToDate(),
      reportType: 'general',
      productIds: [],
      storeIds: [],
      equipmentIds: [],
      masterEquipmentIds: [],
      allocation1Ids: [],
      costCentreIds: []
    };
      
     // Also clear column search filters
    this.columnSearchFilters = {};
    
    this.updateAvailableColumns();
    this.currentPage = 1;
    this.loadComparisonData();
    this.showNotification('Success', 'Filters reset', 'success');
    },
    
    clearColumnSearches: function() {
      this.columnSearchFilters = {};
      this.currentPage = 1;
    },
    
    refreshData: function() {
      this.showNotification('Info', 'Refreshing quick dispensing data...', 'primary');
      this.loadComparisonData();
    },
    
    exportData: function() {
      var self = this;
      var data = this.displayData;
      var headers = this.visibleColumns.map(function(col) {
        return col.label;
      });
      
      var csvRows = data.map(function(record) {
        return self.visibleColumns.map(function(col) {
          var value = record[col.key];
          
          // Format specific columns
          if (col.key === 'transactionDate') {
            value = self.formatDateTime(value);
          } else if (col.key === 'volume' || col.key === 'avgVolume') {
            value = self.formatVolume(value);
          } else if (col.key === 'totalCost' || col.key === 'avgCost' || col.key === 'fuelPrice') {
            value = self.formatCurrency(value);
          }
          
          return '"' + (value || '') + '"';
        }).join(',');
      });
      
      var csvContent = [headers.join(',')].concat(csvRows).join('\n');
      
      var blob = new Blob([csvContent], { type: 'text/csv' });
      var url = window.URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'fams_quick_dispensing_' + this.filters.reportType + '_' + new Date().getTime() + '.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      
      this.showNotification('Success', 'Data exported successfully', 'success');
    },
    
    popOutTable: function() {
      // Create a new window with the current table data
      var self = this;
      var popupWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      
      if (!popupWindow) {
        this.showNotification('Error', 'Please allow popups for this site', 'danger');
        return;
      }
      
      // Build HTML for the popup window
      var html = '<!DOCTYPE html><html><head>';
      html += '<title>FAMS Quick Dispensing - ' + this.getReportTitle() + ' - ' + new Date().toLocaleDateString() + '</title>';
      html += '<style>';
      html += 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 20px; }';
      html += 'h1 { color: #FF8C00; font-size: 1.5rem; margin-bottom: 20px; }';
      html += 'table { width: 100%; border-collapse: collapse; }';
      html += 'th { background: #FF8C00; color: white; padding: 12px; text-align: left; position: sticky; top: 0; }';
      html += 'td { padding: 10px; border-bottom: 1px solid #e0e0e0; }';
      html += 'tr:hover { background: #f5f5f5; }';
      html += '.product-diesel { color: #4CAF50; font-weight: bold; }';
      html += '.product-petrol { color: #2196F3; font-weight: bold; }';
      html += '.product-gas { color: #FF9800; font-weight: bold; }';
      html += '.cost-primary { color: #FF9800; font-weight: bold; }';
      html += '.volume-primary { color: #FF8C00; font-weight: bold; }';
      html += '.info { margin-bottom: 15px; color: #666; }';
      html += '</style>';
      html += '</head><body>';
      html += '<h1>' + this.getReportTitle() + '</h1>';
      html += '<div class="info">Generated: ' + new Date().toLocaleString() + '</div>';
      html += '<div class="info">Total Records: ' + this.displayData.length + '</div>';
      html += '<div class="info">Date Range: ' + this.filters.fromDate + ' to ' + this.filters.toDate + '</div>';
      html += '<table><thead><tr>';
      
      // Add headers based on visible columns
      this.visibleColumns.forEach(function(col) {
        html += '<th>' + col.label + '</th>';
      });
      html += '</tr></thead><tbody>';
      
      // Add data rows
      this.displayData.forEach(function(record) {
        html += '<tr>';
        self.visibleColumns.forEach(function(col) {
          var value = '';
          var className = '';
          
          switch(col.key) {
            case 'transactionDate':
              value = self.formatDateTime(record.transactionDate);
              break;
            case 'volume':
            case 'avgVolume':
              value = self.formatVolume(record[col.key]) + ' L';
              className = 'volume-primary';
              break;
            case 'totalCost':
            case 'avgCost':
              value = 'ZAR ' + self.formatCurrency(record[col.key]);
              className = 'cost-primary';
              break;
            case 'fuelPrice':
              value = 'ZAR ' + self.formatCurrency(record.fuelPrice) + '/L';
              className = 'cost-primary';
              break;
            case 'product':
              value = record.product;
              className = self.getProductClass(record.product);
              break;
            default:
              value = record[col.key] || 'N/A';
          }
          
          html += '<td class="' + className + '">' + value + '</td>';
        });
        html += '</tr>';
      });
      
      html += '</tbody></table>';
      html += '</body></html>';
      
      popupWindow.document.write(html);
      popupWindow.document.close();
      
      this.showNotification('Success', 'Table opened in new window', 'success');
    },
    
    handleRowsChange: function() {
      this.currentPage = 1;
    },
    
    handleAutoRefreshToggle: function() {
      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
        this.showNotification('Success', 'Auto-refresh enabled', 'success');
      } else {
        this.stopAutoRefresh();
        this.showNotification('Info', 'Auto-refresh disabled', 'primary');
      }
    },
    
    handleRefreshIntervalChange: function() {
      if (this.autoRefreshEnabled) {
        this.stopAutoRefresh();
        this.startAutoRefresh();
      }
    },
    
    startAutoRefresh: function() {
      var self = this;
      this.stopAutoRefresh();
      
      if (this.autoRefreshEnabled) {
        this.refreshTimer = setInterval(function() {
          self.loadComparisonData();
        }, this.refreshInterval);
      }
    },
    
    stopAutoRefresh: function() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    
    formatRefreshInterval: function() {
      var seconds = this.refreshInterval / 1000;
      if (seconds < 60) {
        return seconds + 's';
      } else {
        return (seconds / 60) + 'm';
      }
    },
    
    showNotification: function(title, text, type) {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: title,
          text: text,
          color: type,
          position: 'top-right'
        });
      } else {
        console.log(title + ': ' + text);
      }
    },
    
    handleClickOutside: function(event) {
      var self = this;
      var multiSelectContainers = this.$el.querySelectorAll('.multi-select-container');
      var clickedInside = false;
      
      multiSelectContainers.forEach(function(container) {
        if (container.contains(event.target)) {
          clickedInside = true;
        }
      });
      
      if (!clickedInside) {
        Object.keys(this.dropdownStates).forEach(function(key) {
          self.dropdownStates[key] = false;
        });
      }
    }
  },

  mounted: function() {
    this.updateAvailableColumns();
    this.loadComparisonData();
    
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    }
    
    document.addEventListener('click', this.handleClickOutside);
    
    this.showNotification('Welcome!', 'FAMS Quick Dispensing Monitor - Ready', 'success');
  },

  beforeDestroy: function() {
    this.stopAutoRefresh();
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style>
/* COMPLETE STYLE SECTION - EXACTLY AS IN WORKING COMPONENT */
:root {
  --android-color: #4CAF50;
  --log-color: #FF9800;
  --iot-color: #2196F3;
  --all-systems-color: #27AE60;
  --missing-color: #E74C3C;
  --variance-ok: #4CAF50;
  --variance-significant: #F44336;
  
  --autumn-primary: #D2691E;
  --autumn-accent: #FF8C00;
  --gradient-primary: linear-gradient(135deg, var(--autumn-primary) 0%, var(--autumn-accent) 100%);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #e2e8f0;
  height: 100vh;
  overflow: hidden;
  width: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: none;
}

/* Header */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.loader-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: white;
}

.toggle-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.interval-text {
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
}

.interval-select {
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
}

.interval-select option {
  background: #FF8C00;
  color: white;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.interval-select:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.3);
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.auto-refresh {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

/* Main Content */
.main-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  width: 100%;
  background: #e2e8f0;
}

/* Statistics Section */
.stats-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.toggle-stats-btn, .toggle-filter-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.toggle-filter-btn:hover {
  background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 140, 0, 0.3);
}

.toggle-filter-btn .toggle-icon {
  font-size: 1rem;
}

.stats-wrapper {
  padding: 16px 20px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  position: relative;
  overflow: visible;
}

.clickable-stat {
  cursor: pointer;
}

.clickable-stat:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  background: #ffffff !important;
}

.clickable-stat:hover .stat-hint {
  opacity: 1;
}

.stat-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 0.625rem;
  color: #94a3b8;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-stat { border-left: 4px solid #FF8C00; }
.volume-stat { border-left: 4px solid #4CAF50; }
.cost-stat { border-left: 4px solid #FF9800; }
.equipment-stat { border-left: 4px solid #2196F3; }
.product-stat { border-left: 4px solid #9C27B0; }
.average-stat { border-left: 4px solid #E74C3C; }

.stat-label {
  color: #2c3e50;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50;
  font-weight: bold;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive { color: #27AE60; }
.stat-change.negative { color: #E74C3C; }
.stat-change.warning { color: #F39C12; }

/* Search/Filter Section */
.search-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid #cbd5e1;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

/* Filter Header Buttons Container */
.filter-header-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

/* Reset Filters Header Button */
.reset-filter-header-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.reset-filter-header-btn:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.reset-filter-header-btn .reset-icon {
  font-size: 1.1rem;
  font-weight: bold;
  display: inline-block;
  transition: transform 0.3s ease;
}

.reset-filter-header-btn:hover .reset-icon {
  transform: rotate(180deg);
}

/* When filters are collapsed */
.filter-card.collapsed {
  min-height: auto;
}

.filter-card.collapsed .filter-header {
  border-radius: 12px;
  border-bottom: none;
}

/* When search section is collapsed */
.search-section.collapsed {
  margin-bottom: 10px;
}

.search-section.collapsed .filter-header {
  border-radius: 12px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
}

.search-controls-wrapper {
  padding: 16px 20px;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.search-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.search-input:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
}

/* Multi-select Dropdown */
.multi-select-container {
  position: relative;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  min-height: 44px;
}

.multi-select-display:hover {
  border-color: #D2691E;
  background: #fff8f3;
}

.placeholder {
  color: #999;
}

.selected-items {
  color: #495057;
  font-weight: 500;
}

.dropdown-arrow {
  color: #999;
  font-size: 0.75rem;
  transition: transform 0.3s ease;
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #D2691E;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.select-all-option {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #f8f9fa;
}

.dropdown-options {
  max-height: 200px;
  overflow-y: auto;
}

.checkbox-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.checkbox-option:hover {
  background: #fff3e0;
}

.checkbox-option input[type="checkbox"] {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #D2691E;
}

.multi-badge {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 4px;
}

.orange-badge {
  background: #FF8C00;
}

.columns-select {
  border: 2px solid #FF8C00;
  background: #FFF8F3;
}

.columns-dropdown {
  max-height: 400px;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.apply-filters-btn, .reset-filters-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
}

.apply-filters-btn:hover {
  background: linear-gradient(135deg, #B85918 0%, #E67C00 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(210, 105, 30, 0.3);
}

.reset-filters-btn {
  background: white;
  color: #34495E;
  border: 2px solid #FFE4B5;
}

.reset-filters-btn:hover {
  background: #FFF8F3;
  border-color: #FF8C00;
  transform: translateY(-1px);
}

/* Grid Container */
.grid-container {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.rows-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rows-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.rows-select {
  padding: 6px 12px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 100px;
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 16px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  white-space: nowrap;
}

.action-button:hover {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-icon {
  font-size: 1rem;
}

.export-btn {
  color: #10B981;
  border-color: #D1FAE5;
}

.export-btn:hover {
  background: #ECFDF5;
  border-color: #10B981;
}

.popout-btn {
  color: #8B5CF6;
  border-color: #DDD6FE;
}

.popout-btn:hover {
  background: #F3E8FF;
  border-color: #8B5CF6;
}

.refresh-btn {
  color: #3B82F6;
  border-color: #DBEAFE;
}

.refresh-btn:hover {
  background: #EFF6FF;
  border-color: #3B82F6;
}

/* Table */
.table-container {
  overflow: auto;
  position: relative;
  max-height: 600px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
  cursor: pointer;
}

.data-table tbody tr:hover {
  background: #f1f5f9;
}

.data-table tbody tr.row-diesel {
  background: rgba(76, 175, 80, 0.03);
}

.data-table tbody tr.row-petrol {
  background: rgba(33, 150, 243, 0.03);
}

.data-table tbody tr.row-high-volume {
  border-left: 3px solid #FF8C00;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #334155;
}

/* Column Specific Styles - Updated for colorful design */
.transaction-id {
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: #64748b;
}

.transaction-id.colorful {
  color: #ec4899;
  background: rgba(236, 72, 153, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.id-code {
  font-family: monospace;
  font-size: 0.75rem;
  color: #64748b;
}

/* Date and Time Cells */
.date-time-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-part {
  font-size: 0.875rem;
  color: #334155;
  font-weight: 500;
}

.time-ago {
  font-size: 0.75rem;
  color: #94a3b8;
  font-style: italic;
}

.date-cell {
  font-family: monospace;
  color: #475569;
}

.time-cell {
  font-family: monospace;
  color: #64748b;
  font-size: 0.875rem;
}

/* Device/Equipment Styles */
.device-code {
  background: #f0f9ff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #0369a1;
}

.equipment-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Registration Badge */
.registration-badge {
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: monospace;
  letter-spacing: 0.5px;
}

/* Store Name - Regular style */
.store-name {
  color: #64748b;
  font-size: 0.875rem;
}

/* Store Badges - Colorful style */
.store-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

/* Specific store colors */
.store-badge.store-diesel {
  background: linear-gradient(135deg, #065f46 0%, #047857 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(4, 120, 87, 0.2);
}

.store-badge.store-bowser {
  background: linear-gradient(135deg, #7c2d12 0%, #9a3412 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(154, 52, 18, 0.2);
}

.store-badge.store-north {
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
}

.store-badge.store-south {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.store-badge.store-east {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
}

.store-badge.store-west {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.store-badge.store-central {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(6, 182, 212, 0.2);
}

.store-badge.store-main {
  background: linear-gradient(135deg, #be123c 0%, #e11d48 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(225, 29, 72, 0.2);
}

.store-badge.store-vgl {
  background: linear-gradient(135deg, #166534 0%, #15803d 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(21, 128, 61, 0.2);
}

.store-badge.store-a4g {
  background: linear-gradient(135deg, #6b21a8 0%, #9333ea 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(147, 51, 234, 0.2);
}

/* Generic store type colors (based on alphabetical grouping) */
.store-badge.store-type-1 {
  background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(56, 189, 248, 0.2);
}

.store-badge.store-type-2 {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.2);
}

.store-badge.store-type-3 {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(52, 211, 153, 0.2);
}

.store-badge.store-type-4 {
  background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(244, 114, 182, 0.2);
}

.store-badge.store-type-5 {
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(167, 139, 250, 0.2);
}

.store-badge.store-default {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(148, 163, 184, 0.2);
}

/* Hover effect for all store badges */
.store-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Equipment Name */
.equipment-name {
  color: #2196F3;
  font-weight: 500;
}

/* Product Badges - New colorful style */
.product-badge-new {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  cursor: default;
}

.product-badge-new.product-diesel {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);
}

.product-badge-new.product-diesel:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(34, 197, 94, 0.3);
}

.product-badge-new.product-petrol {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.product-badge-new.product-gas {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.2);
}

.product-badge-new.product-other {
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

/* Volume Cell - New colorful style */
.volume-cell-new {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  font-weight: 700;
  color: #f97316;
}

.volume-value {
  font-size: 1rem;
  color: #f97316;
}

.volume-unit {
  font-size: 0.875rem;
  color: #fb923c;
  font-weight: 500;
}

.volume-primary {
  font-weight: 600;
  color: #FF8C00;
  font-size: 0.9rem;
}

/* Activity Type Badge */
.activity-type-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.activity-type-badge.activity-complete {
  background: #10b981;
  color: white;
}

.activity-type-badge.activity-partial {
  background: #f59e0b;
  color: white;
}

.activity-type-badge.activity-pending {
  background: #6b7280;
  color: white;
}

/* Details Cell - New style */
.details-cell-new {
  font-size: 0.8rem;
  color: #64748b;
}

.details-text {
  font-style: italic;
}

/* Status Cell - New colorful style */
.status-cell-new {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot-new {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot-new.processed {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  animation: pulse-green 2s infinite;
}

.status-text-new {
  font-size: 0.8rem;
  color: #10b981;
  font-weight: 600;
}

@keyframes pulse-green {
  0% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2); }
  70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2); }
}

/* Cost and Price Styles */
.cost-primary {
  font-weight: 600;
  color: #FF9800;
  font-size: 0.9rem;
}

.price-value {
  color: #FF9800;
  font-weight: 500;
}

/* Personnel Styles */
.personnel-name {
  color: #9C27B0;
  font-weight: 500;
}

/* Administrative Styles */
.cost-centre {
  color: #00BCD4;
  font-style: italic;
}

.allocation-text {
  color: #00ACC1;
  font-size: 0.875rem;
}

.measurement-text {
  color: #607D8B;
  font-size: 0.875rem;
}

.description-text {
  color: #64748b;
  font-style: italic;
  font-size: 0.875rem;
}

.rebate-text {
  color: #4CAF50;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Job Code */
.job-code {
  background: #f0f9ff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #0369a1;
  font-family: monospace;
}

/* Metric Values */
.metric-value {
  font-weight: 500;
  color: #475569;
}

/* Tag Code */
.tag-code {
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #334155;
  font-family: monospace;
}

.info-code {
  background: #e0f2fe;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  color: #0284c7;
  font-family: monospace;
}

/* Group Name */
.group-name {
  font-weight: 600;
  color: #334155;
  font-size: 0.95rem;
}

/* Count Value */
.count-value {
  font-weight: 700;
  color: #FF8C00;
  font-size: 1rem;
}

/* Account Name */
.account-name {
  font-weight: 500;
  color: #475569;
}

/* Date Range Labels */
.grid-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.report-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #334155;
}

.date-range-label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
  font-style: italic;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.popup-date-range {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

/* Master Group Badges */
.master-group-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.master-group-transport {
  background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(249, 115, 22, 0.2);
}

.master-group-production {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.master-group-workshop {
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(167, 139, 250, 0.2);
}

.master-group-operations {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(6, 182, 212, 0.2);
}

.master-group-maintenance {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.master-group-admin {
  background: linear-gradient(135deg, #ca8a04 0%, #facc15 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(250, 204, 21, 0.2);
}

.master-group-fleet {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.master-group-logistics {
  background: linear-gradient(135deg, #be123c 0%, #f43f5e 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(244, 63, 94, 0.2);
}

.master-group-type-1 {
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  color: white;
}

.master-group-type-2 {
  background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
  color: white;
}

.master-group-type-3 {
  background: linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%);
  color: white;
}

.master-group-type-4 {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
}

.master-group-default {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%);
  color: white;
}

/* Allocation Badges */
.allocation-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.allocation-project {
  background: linear-gradient(135deg, #2563eb 0%, #60a5fa 100%);
  color: white;
}

.allocation-department {
  background: linear-gradient(135deg, #16a34a 0%, #4ade80 100%);
  color: white;
}

.allocation-division {
  background: linear-gradient(135deg, #9333ea 0%, #c084fc 100%);
  color: white;
}

.allocation-branch {
  background: linear-gradient(135deg, #ea580c 0%, #fb923c 100%);
  color: white;
}

.allocation-region {
  background: linear-gradient(135deg, #0891b2 0%, #22d3ee 100%);
  color: white;
}

.allocation-cost {
  background: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
  color: white;
}

.allocation-budget {
  background: linear-gradient(135deg, #65a30d 0%, #84cc16 100%);
  color: white;
}

.allocation-contract {
  background: linear-gradient(135deg, #7c2d12 0%, #c2410c 100%);
  color: white;
}

.allocation-type-1 {
  background: linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%);
  color: white;
}

.allocation-type-2 {
  background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
  color: white;
}

.allocation-type-3 {
  background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
  color: white;
}

.allocation-type-4 {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  color: white;
}

.allocation-default {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  color: white;
}

/* Cost Centre Badges */
.cost-centre-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.cost-centre-transport {
  background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
  color: white;
}

.cost-centre-production {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
}

.cost-centre-workshop {
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  color: white;
}

.cost-centre-odb {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  color: white;
}

.cost-centre-admin {
  background: linear-gradient(135deg, #ca8a04 0%, #facc15 100%);
  color: white;
}

.cost-centre-finance {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
}

.cost-centre-hr {
  background: linear-gradient(135deg, #ec4899 0%, #f472b6 100%);
  color: white;
}

.cost-centre-it {
  background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%);
  color: white;
}

.cost-centre-type-1 {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: white;
}

.cost-centre-type-2 {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  color: white;
}

.cost-centre-type-3 {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: white;
}

.cost-centre-type-4 {
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  color: white;
}

.cost-centre-default {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%);
  color: white;
}

/* Employee Badges (for Drivers and Fuel Attendants) */
.employee-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.employee-type-1 {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
}

.employee-type-2 {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
}

.employee-type-3 {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
}

.employee-type-4 {
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  color: white;
}

.employee-type-5 {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
  color: white;
}

.employee-default {
  background: linear-gradient(135deg, #64748b 0%, #94a3b8 100%);
  color: white;
}

/* Hover effects for all badges */
.master-group-badge:hover,
.allocation-badge:hover,
.cost-centre-badge:hover,
.employee-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Equipment Badges */
.equipment-badge {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.equipment-truck {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
  color: white;
}

.equipment-excavator {
  background: linear-gradient(135deg, #ca8a04 0%, #fbbf24 100%);
  color: white;
}

.equipment-loader {
  background: linear-gradient(135deg, #0c4a6e 0%, #0ea5e9 100%);
  color: white;
}

.equipment-dozer {
  background: linear-gradient(135deg, #a21caf 0%, #d946ef 100%);
  color: white;
}

.equipment-crane {
  background: linear-gradient(135deg, #15803d 0%, #22c55e 100%);
  color: white;
}

.equipment-forklift {
  background: linear-gradient(135deg, #c2410c 0%, #fb923c 100%);
  color: white;
}

.equipment-generator {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
}

.equipment-compressor {
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
  color: white;
}

.equipment-vehicle {
  background: linear-gradient(135deg, #7c2d12 0%, #dc2626 100%);
  color: white;
}

.equipment-master {
  background: linear-gradient(135deg, #581c87 0%, #9333ea 100%);
  color: white;
}

.equipment-type-1 {
  background: linear-gradient(135deg, #2563eb 0%, #60a5fa 100%);
  color: white;
}

.equipment-type-2 {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
}

.equipment-type-3 {
  background: linear-gradient(135deg, #dc2626 0%, #f87171 100%);
  color: white;
}

.equipment-type-4 {
  background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
  color: white;
}

.equipment-default {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  color: white;
}

.equipment-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-approved {
  background: rgba(39, 174, 96, 0.1);
  color: #27AE60;
  border: 1px solid rgba(39, 174, 96, 0.3);
}

.status-rejected {
  background: rgba(231, 76, 60, 0.1);
  color: #E74C3C;
  border: 1px solid rgba(231, 76, 60, 0.3);
}

.status-pending {
  background: rgba(255, 152, 0, 0.1);
  color: #FF9800;
  border: 1px solid rgba(255, 152, 0, 0.3);
}

.status-default {
  background: rgba(158, 158, 158, 0.1);
  color: #9E9E9E;
  border: 1px solid rgba(158, 158, 158, 0.3);
}

/* Text Fields */
.reference-text {
  color: #64748b;
  font-style: italic;
  font-size: 0.875rem;
}

.comments-text {
  color: #475569;
  font-size: 0.875rem;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}

.comments-text:hover {
  overflow: visible;
  white-space: normal;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  position: relative;
  z-index: 100;
  max-width: 300px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #334155;
  background: #f8fafc;
  border-radius: 8px;
  margin: 20px;
}

.empty-icon {
  font-size: 4rem;
  color: #FFE4B5;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid #FFE4B5;
  border-top: 4px solid #D2691E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pagination */
.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f1f5f9;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  gap: 4px;
}

.page-btn {
  padding: 6px 12px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  min-width: 40px;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #D2691E;
  color: #D2691E;
}

.page-btn.active {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-color: #D2691E;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Popup Styles */
.simple-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

/* Drill-down overlay with higher z-index */
.drill-down-overlay {
  z-index: 99999 !important; /* Ensure it's above header */
  background: rgba(0, 0, 0, 0.6);
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.wide-popup {
  max-width: 900px;
}

/* Drill-down Popup Styles */
.drill-down-popup {
  position: fixed !important;
  max-width: 1200px;
  width: 90%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  border: 2px solid #FF8C00;
  z-index: 100000;
}

/* Draggable header */
.drill-down-header {
  cursor: move;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.drill-down-header:active {
  cursor: grabbing;
}

/* Close button enhancement */
.popup-header .close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  font-weight: 300;
  line-height: 1;
}

.popup-header .close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.popup-header .close-btn:active {
  transform: scale(0.95);
}

.drill-down-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-value-large {
  font-size: 1.5rem;
  font-weight: 700;
  color: #FF8C00;
}

.summary-stats {
  display: flex;
  gap: 30px;
  align-items: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.stat-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #334155;
}

/* Drill-down Actions */
.drill-down-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  padding: 0 16px;
}

.action-btn {
  padding: 8px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: white;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.action-btn span {
  font-size: 1rem;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.excel-btn:hover {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.csv-btn:hover {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.print-btn:hover {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

/* Drill-down Grid */
.drill-down-grid {
  flex: 1;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 0 16px;
}

.drill-down-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.drill-down-table thead {
  background: #f1f5f9;
  position: sticky;
  top: 0;
  z-index: 10;
}

.drill-down-table th {
  padding: 10px;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
}

.drill-down-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s ease;
}

.drill-down-row {
  cursor: pointer;
}

.drill-down-row:hover {
  background: #f8fafc;
}

.drill-down-table td {
  padding: 10px;
  font-size: 0.875rem;
  color: #334155;
}

/* Small badges for drill-down table */
.store-badge-small {
  padding: 3px 8px;
  font-size: 0.75rem;
  border-radius: 12px;
  display: inline-block;
}

.product-badge-small {
  padding: 3px 8px;
  font-size: 0.75rem;
  border-radius: 12px;
  display: inline-block;
}

.registration-code {
  font-family: monospace;
  font-size: 0.8rem;
  background: #f0f9ff;
  padding: 2px 6px;
  border-radius: 4px;
  color: #0369a1;
}

.volume-cell-drill {
  font-weight: 600;
  color: #f97316;
  font-size: 0.9rem;
}

/* Drill-down Totals */
.drill-down-totals {
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
}

.total-label {
  font-weight: 600;
  color: #475569;
  font-size: 0.95rem;
}

.total-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #FF8C00;
}

/* Drill-down Footer */
.drill-down-footer {
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  text-align: right;
  border-radius: 0 0 8px 8px;
}

.record-count {
  font-size: 0.875rem;
  color: #64748b;
}

/* Ensure popup body has proper spacing */
.drill-down-popup .popup-body {
  padding: 20px 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Column Search Styles for General View */
.search-row {
  background: #fff3e0 !important;
}

.search-row th {
  background: transparent !important;
  padding: 6px !important;
  border-right: 1px solid #ffcc80 !important;
}

.search-cell {
  padding: 6px !important;
}

.column-search-input {
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #ffcc80;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  text-transform: none;
  font-weight: normal;
  letter-spacing: normal;
}

.column-search-input:focus {
  outline: none;
  border-color: #ff9800;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.2);
  background: #fffbf8;
}

.column-search-input::placeholder {
  color: #999;
  font-style: italic;
  text-transform: none;
}

/* Clear Column Searches Button */
.clear-column-searches-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 10px;
}

.clear-column-searches-btn:hover {
  background: linear-gradient(135deg, #ff5252 0%, #ff4040 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.3);
}

.clear-column-searches-btn .action-icon {
  font-size: 1rem;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  padding: 20px;
}

.detail-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  color: #334155;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-icon {
  font-size: 1.25rem;
}

.equipment-section { border-left: 3px solid #3498DB; }
.cost-section { border-left: 3px solid #FF9800; }
.personnel-section { border-left: 3px solid #9C27B0; }

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.detail-row {
  font-size: 0.9rem;
}

.detail-row strong {
  color: #34495E;
  display: inline-block;
  min-width: 100px;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }
  
  .stats-container {
    grid-template-columns: 1fr 1fr;
  }
  
  .search-controls {
    grid-template-columns: 1fr;
  }
  
  .data-table {
    font-size: 0.75rem;
  }
  
  .data-table th, .data-table td {
    padding: 8px;
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    gap: 15px;
  }
  
  .stats-container {
    grid-template-columns: 1fr;
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .pagination-controls {
    justify-content: center;
  }
}

.header-refresh-btn {
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 12px;
}

.header-refresh-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.header-refresh-btn:active {
  transform: translateY(0);
}

.refresh-icon {
  font-size: 1rem;
  display: inline-block;
  transition: transform 0.3s ease;
}

.header-refresh-btn:hover .refresh-icon {
  transform: rotate(180deg);
}

/* Store Multi-Select Specific Styling */
.search-group:has(.store-multi-select) .multi-select-display {
  font-size: 0.8rem; /* Smaller font for display */
}

.search-group:has(.store-multi-select) .dropdown-options {
  font-size: 0.75rem; /* Smaller font for options */
}

.search-group:has(.store-multi-select) .checkbox-option span {
  font-size: 0.75rem; /* Smaller font for option text */
}

.search-group:has(.store-multi-select) .select-all-option {
  font-size: 0.8rem; /* Smaller font for "Select All" */
}
</style>